import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export type Language = "en" | "ar";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
  dir: "ltr" | "rtl";
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const LANGUAGE_KEY = "finance-tracker-language";

// ─── Translation dictionaries ───────────────────────────────────────────────

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // App
    "app.title": "Mizano",
    "app.subtitle": "Smart Saving, Clear Tracking",
    "app.signIn": "Sign in",
    "app.signInDesc": "Read your bank statements, track every dinar. Sign in to get started.",

    // Account type and business waitlist
    "accountType.eyebrow": "Your Mizano account",
    "accountType.title": "Choose how you use Mizano",
    "accountType.description": "Select the account type that matches what you want to track. You can change this later from Account Settings.",
    "accountType.individualTitle": "Individual",
    "accountType.individualDescription": "Track your own money. Cash flow, budgets and spending from your bank statements.",
    "accountType.businessTitle": "Business",
    "accountType.businessDescription": "Run your company's books. Everything in Individual, plus invoices, receivables, payables and financial statements.",
    "workspace.choose": "Choose your workspace",
    "workspace.individual": "Individual",
    "workspace.business": "Business",
    "workspace.individualDescription": "Personal banking, budgets, and cash-flow tracking.",
    "workspace.businessDescription": "Entity documents, invoices, reconciliation, and statements.",
    "workspace.inDevelopment": "In development",
    "workspace.continueIndividual": "Continue to Individual sign in",
    "workspace.continueBusiness": "Continue to Business sign in",
    "workspace.switch": "Switch workspace",
    "workspace.unavailable": "Business workspace is unavailable. Choose your business setup or join the waitlist.",
    "accountType.continueTitle": "Continue with Individual",
    "accountType.continueDescription": "You can change this choice later from Account Settings. Your data is never deleted when you switch account type.",
    "accountType.businessDetails": "Business details",
    "accountType.businessDetailsDescription": "These details will become your business entity in a later phase.",
    "accountType.companyName": "Company name",
    "accountType.companyNameOptional": "Company name (optional)",
    "accountType.companyNameAr": "Company name in Arabic (optional)",
    "accountType.crNumber": "CR number (optional)",
    "accountType.fiscalMonth": "Fiscal year end month",
    "accountType.fiscalDay": "Fiscal year end day",
    "accountType.email": "Email address",
    "accountType.waitlistTitle": "Business accounts are coming soon",
    "accountType.waitlistDescription": "Join the waitlist and we'll email you when business accounts are ready.",
    "accountType.joinWaitlist": "Join the waitlist",
    "accountType.joinWaitlistDescription": "Your personal Mizano account will remain available now. We only need your email to keep you informed.",
    "accountType.continue": "Continue",
    "accountType.back": "Back",
    "accountType.backHome": "Back to Mizano home",
    "accountType.lightMode": "Switch to light mode",
    "accountType.darkMode": "Switch to dark mode",
    "accountType.toggleLanguage": "Switch language",
    "accountType.submitError": "We could not save your account type. Please try again.",
    "settings.accountType": "Account type",
    "settings.currentType": "Current type",
    "settings.accountTypeDescription": "Choose whether Mizano is for your personal finances or your company. Switching to Individual never deletes business data.",
    "settings.changeAccountType": "Change account type",
    "settings.buildInfo": "Build information",
    "settings.buildGit": "Git",
    "settings.buildCheckpoint": "Checkpoint",
    "settings.buildTime": "Built",
    "settings.tabsLabel": "Account Settings sections",
    "settings.tab.profile": "Profile",
    "settings.tab.balance": "Balance status",
    "settings.tab.entities": "Entities",
    "settings.tab.fx": "FX Rates",
    "settings.tab.privacy": "Data & Privacy",
    "account.exportAllTitle": "Export all my data (both workspaces)",
    "accounts.balanceStatusLink": "View balance status in Account Settings",
    "account.exportAllDescription": "Download a complete privacy copy of everything held about your account, including both Personal and Business workspace records.",
    "account.exportAllJson": "Export all data as JSON",
    "account.exportAllCsv": "Export all transactions as CSV",
    "account.exportAllHint": "Use the Export page for one workspace only. This Account Settings export includes both workspaces and your account profile.",
    "businessDataClear.title": "Clear business data",
    "businessDataClear.description": "Remove all data from this Business workspace while keeping the business profile and chart of accounts.",
    "businessDataClear.start": "Clear business data",
    "businessDataClear.warning": "This action is irreversible. There is no undo.",
    "businessDataClear.previewTitle": "Business data scheduled for permanent deletion",
    "businessDataClear.typeExact": "Type this business name exactly to confirm:",
    "businessDataClear.confirmInput": "Business name confirmation",
    "businessDataClear.cancel": "Cancel",
    "businessDataClear.confirm": "Permanently clear business data",
    "businessDataClear.clearing": "Clearing business data…",
    "businessDataClear.retained": "The business profile and seeded chart of accounts will be kept.",
    "businessDataClear.success": "Business data has been cleared. Your business profile and chart of accounts were kept.",
    "businessDataClear.storageIncomplete": "Business records were cleared, but one or more stored files could not be removed. Please contact support before reusing those file names.",
    "businessDataClear.failed": "Business data could not be cleared. No further action was taken by this screen.",
    "businessDataClear.nameMismatch": "Type the business name exactly to confirm this irreversible action.",
    "businessDataClear.documents": "Documents",
    "businessDataClear.transactions": "Transactions",
    "businessDataClear.journalEntries": "Journal entries",
    "businessDataClear.journalLines": "Journal lines",
    "businessDataClear.counterparties": "Counterparties",
    "businessDataClear.counterpartyAliases": "Counterparty aliases",
    "businessDataClear.invoices": "Invoices",
    "businessDataClear.invoiceLines": "Invoice lines",
    "businessDataClear.invoiceMatches": "Invoice matches",
    "businessDataClear.contracts": "Contracts",
    "businessDataClear.contractSchedules": "Contract schedules",
    "businessDataClear.inventoryCounts": "Stock counts",
    "businessDataClear.financialStatementImports": "Financial-statement imports",
    "businessDataClear.openingBalanceDrafts": "Opening-balance drafts",
    "businessDataClear.xbrlFilings": "XBRL filings",
    "businessDataClear.services": "Services",
    "businessDataClear.serviceComponents": "Service components",
    "businessDataClear.bankAccounts": "Business bank accounts",
    "businessDataClear.fileImports": "Bank file imports",
    "businessDataClear.pdfImportAudits": "PDF import audits",
    "businessDataClear.budgets": "Budgets",
    "businessDataClear.parsingErrors": "Parsing errors",
    "businessDataClear.bankSenders": "Bank senders",
    "businessDataClear.ledgerMappings": "Ledger mappings",
    "businessDataClear.storageFiles": "Stored files",
    "qayd.title": "Qayd workbook",
    "qayd.description": "Generate the official macro-enabled Qayd v1.7 workbook for portal upload. The entity Civil ID and confirmed attestation details are required.",
    "qayd.export": "Download Qayd workbook",
    "qayd.workbookDownloadTitle": "Qayd workbook (.xlsm)",
    "qayd.portalInstruction": "The format the MOCI portal accepts. Open it in Excel, press التحقق من البيانات المالية المقيدة, save, then upload.",
    "qayd.exporting": "Preparing Qayd workbook…",
    "qayd.acceptedFormat": "The macro-preserved Qayd workbook is the portal submission format.",
    "qayd.templateVersion": "Template version",
    "qayd.metadataRequired": "Complete Organisation Civil ID and Qayd filing metadata in Entity settings before exporting.",
    "qayd.metadataRequiredTitle": "Qayd filing details required",
    "qayd.metadataRequiredDescription": "The Qayd workbook identifies the organisation and records auditor-confirmed attestation details. Complete the fields below once in Entity Settings before exporting.",
    "qayd.completeMetadata": "Complete Qayd filing details",
    "qayd.statementRequired": "The financial statements must reconcile before a filing export can be generated.",
    "qayd.templateCorrupt": "The verified local Qayd template is unavailable or corrupt. No workbook was generated.",
    "qayd.generated": "Qayd workbook generated. Upload the downloaded file to the Qayd portal.",
    "qayd.bundleTitle": "Complete Qayd filing package (.zip)",
    "qayd.bundleDescription": "One download containing the portal workbook, both XBRL review files, and Arabic and English submission instructions.",
    "qayd.bundleExporting": "Preparing filing package…",
    "qayd.bundleGenerated": "Qayd filing package generated.",
    "qayd.individualDownloads": "Individual files",
    "qayd.exportTimedOut": "Qayd generation is taking longer than one minute. Review filing history before retrying.",
    "qayd.completedIn": "Qayd export completed in {seconds} seconds.",
    "qayd.attestationOptional": "Auditor attestation fields are optional. Leave them blank unless they are supported by the auditor’s report; Mizano never generates or infers them.",
    "qayd.draftXbrl": "XBRL review files use verified official Qayd element names, but their namespace declarations are unverified placeholders. They remain DRAFT review artifacts; use the Qayd workbook for portal upload.",
    "qayd.guideLink": "How to file with Qayd",
    "qayd.guideEyebrow": "Qayd filing guide",
    "qayd.guideTitle": "Prepare and validate your Qayd filing",
    "qayd.guideDescription": "Use this checklist after downloading the Qayd filing package. The portal workbook must be opened and validated in the Excel desktop application before upload.",
    "qayd.guideBack": "Back to Qayd exports",
    "qayd.guideWindowsTitle": "Windows security step required",
    "qayd.guideWindowsDescription": "Windows blocks macros in files downloaded from the internet until the workbook is unblocked.",
    "qayd.guideWindowsNotice": "This applies even when the workbook is correctly signed. Unblock the extracted .xlsm before opening it in Excel, otherwise the validation macro cannot run.",
    "qayd.guideStepsTitle": "Filing procedure",
    "qayd.guideStepsDescription": "Complete every step in order before uploading to the MOCI Qayd portal.",
    "qayd.guideStep1Title": "Extract the package",
    "qayd.guideStep1Description": "Extract the ZIP to a folder. Do not open the workbook while it is still inside the ZIP.",
    "qayd.guideStep2Title": "Windows only: unblock the workbook",
    "qayd.guideStep2Description": "Right-click qayd_v1.7.xlsm, choose Properties, tick Unblock at the bottom of the General tab, then select Apply and OK.",
    "qayd.guideStep3Title": "Use Excel on a computer",
    "qayd.guideStep3Description": "Open the workbook in Microsoft Excel desktop on a computer. Excel Online and phones cannot run the validation macro.",
    "qayd.guideStep4Title": "Enable macro content",
    "qayd.guideStep4Description": "If Excel shows a yellow security bar, select Enable Content.",
    "qayd.guideStep5Title": "Run the Qayd validation",
    "qayd.guideStep5Description": "On the البيانات الاساسية sheet, select التحقق من البيانات المالية المقيدة.",
    "qayd.guideStep6Title": "Review errors and warnings",
    "qayd.guideStep6Description": "Open the الاخطاء و التحذيرات tab and resolve every reported error before submission.",
    "qayd.guideStep7Title": "Save and upload",
    "qayd.guideStep7Description": "Save the validated workbook, then upload the saved .xlsm to the MOCI Qayd portal.",
    "qayd.guideUnblockImageAlt": "Windows File Properties with the Unblock checkbox highlighted",
    "qayd.guideEnableContentImageAlt": "Excel security warning with Enable Content highlighted",
    "qayd.guideValidateImageAlt": "Qayd validation button on the Basic Data sheet highlighted",
    "qayd.guideAttestationTitle": "Auditor attestation before upload",
    "qayd.guideAttestationDescription": "The workbook leaves unsupported auditor-report fields blank by design.",
    "qayd.guideAttestationNotice": "Complete audit number, audit date, auditor registration number, auditor name, and accounting office only from the auditor’s report. Mizano never creates or guesses these values; the Qayd validator will flag missing required attestation information.",
    "qayd.guideReadyTitle": "Ready to prepare your filing?",
    "qayd.guideReadyDescription": "Return to Qayd exports to download the filing package.",
    "qayd.disclosuresTitle": "Qayd Notes disclosures",
    "qayd.disclosuresDescription": "Company narrative disclosures are selected from the official Arabic template according to this reporting period’s Qayd facts.",
    "qayd.disclosuresNonBlocking": "Missing disclosures do not block the workbook download, but the Qayd validator will report each required disclosure left blank.",
    "qayd.disclosuresRequired": "Required for this period",
    "qayd.disclosuresComplete": "All disclosures required for this period are explicitly confirmed.",
    "qayd.disclosuresUnfilled": "Required disclosures remain unfilled or unconfirmed.",
    "qayd.disclosuresManage": "Manage Notes disclosures",
    "qayd.disclosuresEntityBaseline": "Reusable entity disclosure",
    "qayd.disclosuresPeriodOverride": "Period override",
    "qayd.disclosuresDraft": "Draft — not included in the workbook",
    "qayd.disclosuresConfirmed": "Confirmed — included in the workbook",
    "qayd.disclosuresUnfilledState": "Unfilled",
    "qayd.disclosuresSuggestion": "Standard draft suggestion",
    "qayd.disclosuresUseSuggestion": "Use editable suggestion",
    "qayd.disclosuresSaveDraft": "Save draft",
    "qayd.disclosuresConfirm": "Confirm for filing",
    "qayd.disclosuresConfirmAcknowledgement": "I confirm that this is the company’s reviewed disclosure for the selected scope. It may be included in a Qayd workbook.",
    "qayd.disclosuresTextAr": "Arabic disclosure text",
    "qayd.disclosuresTextEn": "English reference text",
    "qayd.disclosuresTextHint": "The Arabic text is written to the official Arabic Qayd template. English is retained as a reviewed reference for the company.",
    "qayd.disclosuresDraftSaved": "Disclosure draft saved. Review and explicitly confirm it before it can be included in a workbook.",
    "qayd.disclosuresConfirmedSaved": "Disclosure confirmed for filing.",
    "qayd.disclosuresError": "The disclosure could not be saved. Review the text and try again.",
    "qayd.disclosuresArabicOnly": "MOCI publishes the official Qayd template in Arabic only. Export Excel statements in English are reference reports and cannot be submitted to Qayd.",
    "qayd.disclosuresPeriod": "Reporting period end",
    "qayd.disclosuresScope": "Disclosure scope",
    "qayd.disclosuresShowAll": "Show all official Notes fields",
    "qayd.disclosuresOnlyRequired": "Show required fields only",
    "qayd.disclosuresNoRequirements": "Not required for the selected period",
    "admin.businessWaitlistTitle": "Business waitlist",
    "admin.businessWaitlistDescription": "People who asked to be notified when business accounts become available.",
    "admin.businessWaitlistTotal": "Total waitlist entries",
    "admin.businessWaitlistEmpty": "No business waitlist entries yet.",
    "admin.businessWaitlistDenied": "This administrative view is not available for your account.",
    "admin.noCompanyName": "No company name provided",
    "admin.businessAccessTitle": "Business access overrides",
    "admin.businessAccessDescription": "Grant or remove Business workspace access for a specific user. Every change is audited.",
    "admin.businessAccessEnabled": "Business access enabled",
    "admin.businessAccessEnable": "Enable Business access",
    "admin.businessAccessEmpty": "No users found.",
    "admin.noEmail": "No email",
    "admin.fxRatesTitle": "FX Rates",
    "admin.fxRatesDescription": "Manage global KWD-per-unit exchange rates by effective date. These rates are available to all users.",
    "admin.fxRatesDenied": "This FX rate management view is not available for your account.",
    "admin.fxRatesAdd": "Add rate",
    "admin.fxRatesCurrency": "Currency",
    "admin.fxRatesRate": "KWD per 1 unit",
    "admin.fxRatesEffective": "Effective date",
    "admin.fxRatesSource": "Source",
    "admin.fxRatesCurrent": "Rate history",
    "admin.fxRatesEmpty": "No FX rates found.",
    "fx.convertedAt": "Converted at",
    "fx.on": "on",
    "ledger.title": "Ledger",
    "ledger.description": "Double-entry accounting for your business entity. Entries must always balance exactly.",
    "ledger.openingTitle": "Opening balances",
    "ledger.openingRequired": "Opening balances are required before Mizano can generate business financial statements.",
    "ledger.openingReady": "Opening balances are recorded. Financial statements can use the ledger.",
    "ledger.account": "Ledger account",
    "ledger.selectAccount": "Select account",
    "ledger.debit": "Debit",
    "ledger.credit": "Credit",
    "ledger.debits": "Total debits",
    "ledger.credits": "Total credits",
    "ledger.remove": "Remove",
    "ledger.addLine": "Add line",
    "ledger.saveOpening": "Save opening balances",
    "ledger.trialBalance": "Trial balance",
    "ledger.trialBalanceDescription": "All active accounts and their debits, credits, and net movement for the selected fiscal year.",
    "ledger.trialBalanceBlocked": "Add balanced opening balances first. Mizano will not generate a balance sheet without them.",
    "ledger.netMovement": "Net movement",
    "ledger.balanced": "Balanced",
    "ledger.unbalanced": "Unbalanced",
    "ledger.adjustmentsTitle": "Reconciliation adjustments",
    "ledger.adjustmentsDescription": "These balance plugs never post automatically. Convert one into a balanced journal entry, or leave it outside the ledger.",
    "ledger.convertAdjustment": "Convert to entry",
    "ledger.createEntry": "Create balanced entry",
    "ledger.noAdjustments": "No reconciliation adjustments need review.",
    "ledger.unknown": "Unknown",
    "ledger.openingWizard": "Document-driven opening balance",
    "ledger.openingWizardDescription": "Choose the route that reflects your entity, attach the supporting documents, then review and confirm a balanced opening journal.",
    "ledger.openingFiscalYear": "Fiscal year",
    "ledger.openingSourceDocuments": "Source documents",
    "ledger.openingSourceDocumentsDescription": "Only documents belonging to the active entity can support this opening balance.",
    "ledger.openingNoDocuments": "No entity documents are available yet. Upload a bank statement or entity document first.",
    "ledger.openingNoSpecificSource": "No line-specific source",
    "ledger.openingSourceDocument": "Supporting document",
    "ledger.openingSide": "Entry side",
    "ledger.openingRoute.prior_statements": "Prior financial statements",
    "ledger.openingRoute.small_company": "Entity documents and bank cash",
    "ledger.openingRoute.new_company": "Newly started company",
    "ledger.openingRouteDescription.prior_statements": "Enter reviewed balances from prior financial statements.",
    "ledger.openingRouteDescription.small_company": "Derive retained earnings from confirmed cash, capital, and optional opening items.",
    "ledger.openingRouteDescription.new_company": "Confirm initial cash paid in equals owner’s capital.",
    "ledger.openingPriorLines": "Prior-statement balances",
    "ledger.openingPriorLinesDescription": "Enter each ledger balance from the selected prior statement, then check the debit and credit totals.",
    "ledger.openingSmallCompanyInputs": "Confirmed opening items",
    "ledger.openingSmallCompanyDescription": "Retained earnings is derived from the confirmed opening position and remains visible for review.",
    "ledger.openingNewCompanyInputs": "Initial funding",
    "ledger.openingNewCompanyDescription": "A new company starts with no retained earnings. Cash paid in must equal capital exactly.",
    "ledger.capitalPaidDate": "Capital payment date",
    "ledger.incorporationDate": "Incorporation date (if confirmed)",
    "ledger.capitalPaidDateHelp": "Enter the date paid-in capital was actually received. An incorporation or licence date is not assumed to be the payment date.",
    "ledger.openingField.cash": "Bank cash",
    "ledger.openingField.capital": "Paid-up capital",
    "ledger.openingField.receivables": "Receivables",
    "ledger.openingField.payables": "Payables",
    "ledger.openingField.fixedAssets": "Fixed assets",
    "ledger.openingField.accumulatedDepreciation": "Accumulated depreciation",
    "ledger.openingField.loans": "Loans",
    "ledger.openingField.cashPaidIn": "Cash paid in",
    "ledger.openingDerivedRetainedEarnings": "Derived retained earnings",
    "ledger.openingNewCompanyUnbalanced": "Cash paid in must equal owner’s capital to KWD 0.001.",
    "ledger.openingNewCompanyRetainedEarnings": "Retained earnings will start at KWD 0.000.",
    "ledger.openingCreateDraft": "Create review draft",
    "ledger.openingDrafts": "Opening balance drafts",
    "ledger.openingDraftsDescription": "Drafts remain review-only until you verify and confirm their balanced lines.",
    "ledger.openingNoDrafts": "No opening-balance drafts yet.",
    "ledger.openingReviewDraft": "Review opening draft",
    "ledger.openingReviewDescription": "Check every line against the source documents before posting. Confirmation creates the opening journal; corrections use a reversal.",
    "ledger.openingDerived": "System-derived from your confirmed inputs",
    "ledger.openingVerification": "I have checked these lines against the listed source documents.",
    "ledger.openingUploadSource": "Upload source document",
    "ledger.openingExtractSources": "Extract and map sources",
    "ledger.openingExtractionFailed": "The source document could not be extracted. Review it manually.",
    "ledger.openingDerivedUnverified": "Derived retained earnings are displayed in the Balance Sheet until you mark this opening balance verified.",
    "ledger.openingMarkVerified": "Mark opening balance verified",
    "ledger.openingConfirm": "Confirm and post opening balance",
    "ledger.openingPostedEntry": "Opening journal entry #{id} is currently active.",
    "ledger.openingReverse": "Reverse opening entry",
    "businessDashboard.eyebrow": "Business workspace",
    "businessDashboard.title": "Business Dashboard",
    "businessDashboard.cashPosition": "Cash position",
    "businessDashboard.cashOnHand": "Cash on hand",
    "businessDashboard.accountCount": "{count} bank accounts",
    "businessDashboard.owedToYou": "Owed to you",
    "businessDashboard.overdueDetail": "Overdue: {amount}",
    "businessDashboard.youOwe": "You owe",
    "businessDashboard.dueNextSeven": "Due in 7 days: {amount}",
    "businessDashboard.netPosition": "Net position",
    "businessDashboard.netPositionCaption": "cash + AR − AP",
    "businessDashboard.reconciliation": "Reconciliation status",
    "businessDashboard.booksTie": "Books tie to bank as of {date} — no unexplained differences.",
    "businessDashboard.booksDiffer": "Books differ from linked bank balances by {amount}.",
    "businessDashboard.notComparable": "Not reconciled — no bank account linked to this entity.",
    "businessDashboard.viewAccount": "View account",
    "businessDashboard.receivablesAging": "Receivables aging",
    "businessDashboard.largestOverdue": "Largest overdue: {name} · {amount}",
    "businessDashboard.unknownCounterparty": "Unassigned counterparty",
    "businessDashboard.needsYou": "Needs you",
    "businessDashboard.nothingToDo": "Nothing needs your attention.",
    "businessDashboard.openingBalances": "Opening balances not yet set",
    "businessDashboard.suggestedMatches": "{count} payments match invoices",
    "businessDashboard.invoicesReview": "{count} invoices need review",
    "businessDashboard.counterpartiesConfirm": "{count} counterparties need confirming",
    "businessDashboard.contractsDue": "{count} contracts renewing or expiring in 30 days",
    "businessDashboard.bankChains": "{count} bank accounts with an unreconciled balance chain",
    "businessDashboard.performanceUnavailable": "Available once opening balances are set.",
    "businessDashboard.revenue": "Revenue",
    "businessDashboard.expenses": "Expenses",
    "businessDashboard.profit": "Profit",
    "businessDashboard.noPriorPeriod": "No prior-period comparison",
    "businessDashboard.priorChange": "Prior period: {percent}",
    "businessDashboard.profitMargin": "Profit margin: {percent}",
    "businessDashboard.noLedgerActivity": "No ledger activity in this period.",
    "businessDashboard.new": "New",
    "businessDashboard.performance": "Ledger performance",
    "businessUpload.title": "Upload business document",
    "businessUpload.description": "Drop a source file. Mizano identifies its type, then you confirm before parsing.",
    "businessUpload.drop": "Drop a PDF or image here, or choose a file",
    "businessUpload.detectedType": "Detected document type",
    "businessUpload.confirmBeforeParse": "Confirm or correct the type before Mizano starts the relevant review workflow.",
    "businessUpload.continue": "Confirm and continue",
    "businessUpload.detecting": "Identifying document type…",
    "businessUpload.detectionFailed": "Could not identify the document. Choose its type to continue.",
    "businessUpload.recentStatementReviews": "Recent financial statement reviews",
    "businessUpload.statementImport": "Statement import",
    "businessUpload.posted": "Posted",
    "businessUpload.linesRemain": "{{count}} lines remain",
    "businessUpload.confidence": "confidence",
    "businessUpload.language": "Language read",
    "businessUpload.language.arabic": "Arabic",
    "businessUpload.language.english": "English",
    "businessUpload.language.bilingual": "Arabic and English",
    "businessUpload.language.unknown": "Language could not be confirmed",
    "businessUpload.receiptSettled": "Paid receipt detected. It will be imported as settled at source and excluded from matching.",
    "businessUpload.auditedEvidence": "Auditor report detected.",
    "businessUpload.unauditedEvidence": "Management or unaudited accounts detected.",
    "businessUpload.rejectionHelp": "Split multi-document files or use a supported source document. Mizano has not imported anything.",
    "businessUpload.kind.bank_statement": "Bank statement",
    "businessUpload.kind.bank_account_statement": "Bank-account statement",
    "businessUpload.kind.credit_card_statement": "Credit-card statement",
    "businessUpload.kind.invoice": "Invoice",
    "businessUpload.kind.receipt": "Paid receipt",
    "businessUpload.kind.contract": "Contract",
    "businessUpload.kind.financial_statement": "Financial statement",
    "businessUpload.kind.audited_financial_statement": "Audited financial statement",
    "businessUpload.kind.management_accounts": "Management accounts",
    "businessUpload.kind.trial_balance": "Trial balance",
    "businessUpload.kind.entity_document": "Entity document",
    "businessUpload.kind.sms": "SMS message",
    "businessUpload.kind.image": "Image",
    "businessUpload.kind.unknown": "Unknown / not importable",
    "businessUpload.kind.quotation": "Quotation",
    "businessUpload.kind.proforma_invoice": "Proforma invoice",
    "businessUpload.kind.purchase_order": "Purchase order",
    "businessUpload.kind.credit_note": "Credit note",
    "businessUpload.kind.debit_note": "Debit note",
    "businessUpload.kind.statement_of_account": "Statement of account",
    "businessUpload.kind.counterparty_directory": "Counterparty directory",
    "businessUpload.kind.delivery_note": "Delivery note",
    "businessUpload.kind.payroll": "Payroll document",
    "businessUpload.kind.multi_document": "Multiple documents",
    "businessUpload.rejection.quotation": "This appears to be a quotation. Nothing is owed until an invoice is issued, so Mizano cannot import it yet.",
    "businessUpload.rejection.proforma_invoice": "This appears to be a proforma invoice. It is not a payable demand, so Mizano cannot import it yet.",
    "businessUpload.rejection.purchase_order": "This appears to be a purchase order. It does not create a payable, so Mizano cannot import it yet.",
    "businessUpload.rejection.credit_note": "This appears to be a credit note. It is an adjustment document, not a negative invoice, so Mizano cannot import it yet.",
    "businessUpload.rejection.debit_note": "This appears to be a debit note. It is an adjustment document, so Mizano cannot import it yet.",
    "businessUpload.rejection.statement_of_account": "This appears to be a counterparty statement of account, not a bank statement, so Mizano cannot import it yet.",
    "businessUpload.rejection.counterparty_directory": "This appears to be a counterparty directory. Mizano cannot safely merge identities from a directory yet, so it has not imported it.",
    "businessUpload.rejection.delivery_note": "This appears to be a delivery note. It does not evidence an invoice, so Mizano cannot import it yet.",
    "businessUpload.rejection.payroll": "This appears to be a payroll document. Payroll import is not supported yet.",
    "businessUpload.rejection.multi_document": "This file contains multiple documents. Split it before importing so each document can be reviewed safely.",
    "businessUpload.rejection.unknown": "Mizano could not determine a supported document type. Nothing has been imported.",
    "businessUpload.rejection.credit_card_statement": "This appears to be a credit-card statement. It has card balances, purchases, payments, and fees rather than a bank-account running balance, so Mizano did not import it.",
    "businessAnalytics.title": "Business Analytics",
    "businessAnalytics.description": "Month-by-month account movement derived from the trial balance.",
    "businessAnalytics.monthByMonth": "Month by month",
    "businessAnalytics.account": "Account",
    "businessAnalytics.total": "Total",
    "businessAnalytics.expenseComposition": "Expense composition",
    "businessAnalytics.revenueByCounterparty": "Revenue by counterparty",
    "businessAnalytics.agingProfile": "AR / AP aging profile",
    "services.title": "Services",
    "services.description": "Operational service prices and average costs for management insight.",
    "services.operationalDisclosure": "Estimated from average costs you entered. Not used in your financial statements.",
    "services.catalogue": "Service catalogue",
    "services.empty": "Add your first service to see operational margins.",
    "services.add": "Add service",
    "services.edit": "Edit",
    "services.save": "Save",
    "services.cancel": "Cancel",
    "services.name": "Service name",
    "services.nameAr": "Arabic service name",
    "services.category": "Category",
    "services.noCategory": "No category",
    "services.unit": "Unit",
    "services.noUnit": "No unit",
    "services.price": "Selling price",
    "services.averageCost": "Average cost",
    "services.margin": "Margin",
    "services.marginPercent": "Margin %",
    "services.lossMaking": "Average cost exceeds the selling price. This service loses money on every sale.",
    "services.recipe": "Recipe / cost-up",
    "services.recipeHint": "One component per line: description | quantity | unit cost. This remains an operational estimate.",
    "services.saveRecipe": "Save cost-up",
    "services.suggestions": "Editable suggestions",
    "services.suggestionsHint": "Suggestions contain no prices or costs. Set your own figures before saving.",
    "services.businessType.restaurant": "Restaurant",
    "services.businessType.salon": "Salon",
    "services.businessType.repair": "Repair",
    "services.businessType.consulting": "Consulting",
    "services.marginsTitle": "Service margins",
    "services.marginUnavailable": "Service margins could not be loaded.",
    "services.unitsSold": "Units sold",
    "services.revenue": "Revenue",
    "services.estimatedCost": "Estimated cost",
    "services.noLinkedSales": "No linked service sales in this period.",
    "services.totalEstimatedCost": "Total estimated cost of services delivered",
    "services.actualCostOfSales": "Actual cost of sales from stock count",
    "services.unexplainedDifference": "Unexplained difference",
    "services.reconciliationHint": "This represents waste, theft, unrecorded consumption, or a mispriced service. It is operational analysis, not accounting.",
    "services.invoiceLineLinks": "Service links on sales invoice lines",
    "services.invoiceLineHint": "These are analytical associations only. They never create a journal entry.",
    "services.addFromInvoiceLine": "Add as a new service",
    "counterparties.openInvoices": "Open invoices",
    "invoice.amendTitle": "Correct and revalidate",
    "invoice.amendResubmit": "Revalidate and resubmit",
    "invoice.invoiceNumberLabel": "Invoice number",
    "invoice.directionLabel": "Direction",
    "invoice.counterpartyLabel": "Counterparty",
    "invoice.currencyLabel": "Currency",
    "invoice.subtotalLabel": "Subtotal",
    "invoice.taxLabel": "Tax",
    "invoice.totalLabel": "Total",
    "businessStatements.title": "Business Statements",
    "businessStatements.description": "Ledger-derived financial statements for your business entity.",
    "businessStatements.start": "Period start",
    "businessStatements.end": "Period end",
    "businessStatements.unavailable": "Business statements are not ready",
    "businessStatements.reportUnavailable": "The statements could not be loaded. Retry, or return to the Ledger to review the opening balance setup.",
    "businessStatements.openingActionTitle": "Opening balances not set",
    "businessStatements.openingMissing": "Record a balanced opening entry before generating financial statements.",
    "businessStatements.openingReversed": "The prior opening entry was reversed and has not been replaced. Create and confirm a new opening entry.",
    "businessStatements.noLedgerActivity": "No ledger activity exists yet. Start by recording and confirming opening balances.",
    "businessStatements.openingWizardLink": "Open opening-balance wizard",
    "businessStatements.openingRequired": "Record balanced opening balances in the Ledger before generating statements.",
    "businessStatements.crNumber": "CR number",
    "businessStatements.noCr": "CR number not recorded",
    "businessStatements.exportPdf": "Export PDF",
    "businessStatements.exportExcel": "Export Excel",
    "businessStatements.exportBlocked": "Export is blocked until the balance sheet is balanced.",
    "businessStatements.allowPopup": "Allow pop-ups to export the PDF.",
    "businessStatements.pdfFailed": "The PDF could not be generated. Please try again.",
    "businessStatements.footer": "Prepared from client-provided documents. Not audited. / أُعد من مستندات العميل. غير مدقق.",
    "inventoryCount.title": "Stock count",
    "inventory.title": "Inventory & Margins",
    "inventory.description": "Ledger-based inventory, cost of sales, and gross margin. Quantities and per-item costs are unavailable because Mizano has no item subledger.",
    "inventory.currentBalance": "Current inventory balance",
    "inventory.grossMargin": "Gross margin",
    "inventory.noItemSubledger": "Mizano does not hold an item subledger, so quantities, SKUs, and per-item costs are unavailable.",
    "inventory.purchaseLines": "Purchase lines",
    "inventory.classifiedLines": "Classified inventory lines",
    "inventory.unclassifiedLines": "Historical lines with classification unknown",
    "inventory.historicalUnknown": "Classified before line detail was recorded.",
    "inventory.supplier": "Supplier",
    "inventory.sourceInvoice": "Source invoice",
    "inventory.lineDescription": "Description",
    "inventory.lineAmount": "Line amount",
    "inventory.classifiedTotal": "Classified inventory-line total",
    "inventory.ledgerDifference": "Difference from account 1300 balance",
    "inventoryCount.description": "Confirm a period-end inventory value. Mizano never estimates stock consumption from sales.",
    "inventoryCount.add": "Record stock count",
    "inventoryCount.date": "Count date",
    "inventoryCount.closing": "Closing inventory value",
    "inventoryCount.method": "Count method",
    "inventoryCount.physical": "Physical count",
    "inventoryCount.estimate": "Management estimate",
    "inventoryCount.notes": "Notes (optional)",
    "inventoryCount.document": "Supporting count-sheet document (optional)",
    "inventoryCount.preview": "Cost of sales calculation",
    "inventoryCount.opening": "Opening inventory",
    "inventoryCount.purchases": "Inventory purchases",
    "inventoryCount.cogs": "Cost of sales",
    "inventoryCount.confirm": "Confirm and post adjustment",
    "inventoryCount.zeroConfirm": "I confirm that closing inventory is zero despite material purchases.",
    "inventoryCount.history": "Confirmed stock counts",
    "inventoryCount.confirmedBy": "Confirmed by",
    "inventoryCount.reversed": "Reversed",
    "inventoryCount.estimateDisclosure": "Cost of sales for this period is based on a management estimate, not a physical stock count.",
    "inventoryCount.physicalDisclosure": "Cost of sales for this period is based on a confirmed physical stock count.",
    "inventoryCount.health": "Inventory is held and revenue was recognised, but no cost of sales has been recognised for this period.",
    "inventoryCount.healthFigures": "Inventory: {inventory} KWD · Revenue: {revenue} KWD",
    "inventoryCount.chartMissingTitle": "Chart of accounts is not set up",
    "inventoryCount.chartMissing": "Stock counts are unavailable until this business has a chart of accounts. Set up or repair the business chart, then try again.",
    "inventoryCount.fileReadFailed": "The supporting document could not be read.",
    "inventoryCount.uploadFailed": "The supporting document could not be uploaded.",
    "invoice.healthInventoryWithoutCogs": "Inventory held with no cost of sales recognised",
    "invoice.healthMissingChart": "Chart of accounts has not been set up",
    "businessStatements.excluded": "Invoices excluded for review",
    "businessStatements.excludedDescription": "These invoices need review and are not included in the ledger-derived statements.",
    "businessStatements.balanceMismatch": "Balance sheet does not balance",
    "businessStatements.income": "Income Statement",
    "businessStatements.balance": "Balance Sheet",
    "businessStatements.cash": "Cash Flow",
    "businessStatements.comparative": "Current period with prior-period comparative",
    "businessStatements.showComparative": "Show prior-period comparative",
    "businessStatements.currentOnly": "Current period only",
    "businessStatements.account": "Account",
    "businessStatements.current": "Current",
    "businessStatements.prior": "Prior",
    "businessStatements.subtotal": "Subtotal",
    "businessStatements.profit": "Profit for the period",
    "businessStatements.assets": "Assets",
    "businessStatements.liabilities": "Liabilities",
    "businessStatements.equity": "Equity",
    "businessStatements.changesInEquity": "Statement of Changes in Equity",
    "businessStatements.trialBalance": "Trial Balance",
    "businessStatements.generalLedger": "General Ledger",
    "xbrl.title": "XBRL export",
    "xbrl.draft": "Draft only — not for portal submission while taxonomy concepts remain unverified.",
    "xbrl.taxonomy": "Taxonomy version",
    "xbrl.unverified": "Unverified concepts",
    "xbrl.unmapped": "Unmapped accounts",
    "xbrl.generateInline": "Generate iXBRL (.xhtml)",
    "xbrl.instanceDownloadTitle": "XBRL instance (.xml)",
    "xbrl.inlineDownloadTitle": "Inline XBRL (.xhtml)",
    "xbrl.reviewDownloadsDescription": "Human-readable and machine-readable review files. They use the same official Qayd tags and aggregation as the workbook, but remain DRAFT files only.",
    "xbrl.generateInstance": "Generate XBRL (.xml)",
    "xbrl.mapping": "Mapping review",
    "xbrl.concept": "Taxonomy concept",
    "xbrl.verified": "Verified",
    "xbrl.notMapped": "Not mapped",
    "xbrl.history": "Generation history",
    "xbrl.facts": "facts",
    "xbrl.blocked": "XBRL generation is blocked until the financial statement checks pass.",
    "xbrl.crRequired": "XBRL generation is blocked because this entity has no CR number. A filing cannot identify the entity without it.",
    "xbrl.editEntity": "Add CR number in Entity Management",
    "businessStatements.opening": "Opening balance",
    "businessStatements.periodDebits": "Period debits",
    "businessStatements.periodCredits": "Period credits",
    "businessStatements.closing": "Closing balance",
    "businessStatements.hideZero": "Hide zero-movement accounts",
    "businessStatements.selectAccount": "Select an account",
    "businessStatements.runningBalance": "Running balance",
    "businessStatements.source": "Source",
    "businessStatements.noLedgerLines": "No journal lines in this period.",
    "businessStatements.generalLedgerDescription": "Select an account to trace each journal line, its source reference, and its running balance.",
    "businessStatements.date": "Date",
    "businessStatements.memo": "Memo",
    "businessStatements.equityDescription": "Capital, retained earnings, and drawings movements derived from the ledger, with three explicit tie-outs.",
    "businessStatements.equityMovement": "Movement",
    "businessStatements.equityCapital": "Capital",
    "businessStatements.equityRetained": "Retained earnings",
    "businessStatements.equityDrawings": "Drawings",
    "businessStatements.equityTotal": "Total equity",
    "businessStatements.equityRow.opening": "Opening balance",
    "businessStatements.equityRow.profit": "Profit for the period",
    "businessStatements.equityRow.contributions": "Owner contributions",
    "businessStatements.equityRow.drawings": "Owner drawings",
    "businessStatements.equityRow.adjustments": "Equity adjustments",
    "businessStatements.equityRow.closing": "Closing balance",
    "businessStatements.profitTieOut": "Profit agrees with Income Statement",
    "businessStatements.equityTieOut": "Closing equity agrees with Balance Sheet",
    "businessStatements.openingTieOut": "Opening equity agrees with prior Balance Sheet",
    "businessStatements.liabilitiesEquity": "Liabilities + equity",
    "businessStatements.balanceCheck": "Assets = liabilities + equity",
    "businessStatements.openingDerivedDisclosure": "Opening retained earnings of KWD {amount} were derived from confirmed opening inputs and remain unverified.",
    "businessStatements.balanced": "Balanced",
    "businessStatements.cashDescription": "Indirect method from the ledger, reconciled to uploaded bank balances.",
    "businessStatements.depreciation": "Depreciation",
    "businessStatements.receivablesMovement": "Movement in receivables",
    "businessStatements.payablesAccruals": "Movement in payables and accruals",
    "businessStatements.operating": "Net operating cash flow",
    "businessStatements.investing": "Net investing cash flow",
    "businessStatements.financing": "Net financing cash flow",
    "businessStatements.closingCash": "Closing cash",
    "businessStatements.cashReconciliation": "Cash reconciliation",
    "businessStatements.cashNotComparable": "Not reconciled — no bank account linked to this entity.",
    "businessStatements.capitalAssumedMinimum": "Capital shown at the KD 1,000 statutory minimum. Update it from your incorporation documents or enter your actual paid-up capital.",
    "businessStatements.importedStatementDisclosure": "Prepared from figures extracted from an uploaded financial statement, not from underlying transaction records.",
    "ledger.statementImportTitle": "Import a complete financial statement",
    "ledger.statementImportDescription": "Extract the full balance sheet and profit or loss, map every line, reconcile it, then post two traceable entries.",
    "ledger.statementExtract": "Extract full statement",
    "ledger.statementSelectDocument": "Select an uploaded financial-statement document first.",
    "ledger.statementLine": "Source line",
    "ledger.statementStatus": "Review status",
    "ledger.statementExclude": "Explicitly exclude",
    "ledger.statementMapped": "Mapped",
    "ledger.statementExcluded": "Excluded",
    "ledger.statementUnmapped": "Mapping required",
    "ledger.statementEquityMovements": "Explicit equity movements",
    "ledger.statementEquityHelp": "Add documented dividends, OCI, capital movements, or other equity changes needed to reconcile. Mizano never plugs retained earnings.",
    "ledger.statementPreview": "Preview reconciliation",
    "ledger.statementPost": "Post reviewed two-entry import",
    "ledger.statementActivityDifference": "Period activity difference",
    "ledger.statementBalanceDifference": "Source balance-sheet difference",
    "ledger.statementDiagnosis": "Diagnosis",
    "businessStatements.statementCash": "Closing cash per statement",
    "businessStatements.bankCash": "Closing bank balances",
    "businessStatements.difference": "Difference",
    "businessStatements.subtype.current_asset": "Current assets",
    "businessStatements.subtype.noncurrent_asset": "Non-current assets",
    "businessStatements.subtype.contra_asset": "Contra assets",
    "businessStatements.subtype.current_liability": "Current liabilities",
    "businessStatements.subtype.noncurrent_liability": "Non-current liabilities",
    "businessStatements.subtype.equity": "Equity",
    "businessStatements.subtype.operating_revenue": "Operating revenue",
    "businessStatements.subtype.other_revenue": "Other income",
    "businessStatements.subtype.cost_of_sales": "Cost of sales",
    "businessStatements.subtype.operating_expense": "Operating expenses",
    "businessStatements.subtype.other_expense": "Other expenses",
    "businessStatements.subtype.other": "Other",
    "landing.businessInDevelopment": "In development",
    "entity.switcher": "Entity",
    "entity.manage": "Manage entities",
    "entity.title": "Entity Management",
    "entity.description": "Create entities and choose which entity's financial data you are viewing.",
    "entity.create": "Create entity",
    "entity.name": "Entity name",
    "entity.nameAr": "Arabic name (optional)",
    "entity.type": "Entity type",
    "entity.company": "Company",
    "entity.soleProprietor": "Sole proprietor",
    "entity.individual": "Individual",
    "entity.businessType": "Business type",
    "entity.businessTypeRetail": "Retail trading",
    "entity.businessTypeServices": "Services",
    "entity.businessTypeBoth": "Retail trading and services",
    "entity.crNumber": "CR number (optional)",
    "entity.organisationCivilId": "Organisation Civil ID (Qayd)",
    "entity.organisationCivilIdHint": "Qayd uses الرقم المدني للجهة. It is different from the CR number and is never copied from it.",
    "entity.invalidOrganisationCivilId": "Enter an organisation Civil ID using digits only.",
    "entity.qaydMetadata": "Qayd filing metadata",
    "entity.qaydMetadataDescription": "Required company facts for a Qayd workbook export. Auditor attestation fields are optional and must be entered only when confirmed by the auditor's report.",
    "entity.qaydAuditorReportOnly": "If supplied, attestation number, date, auditor registration number, auditor name, and accounting office must come from the auditor's report. Mizano never generates or infers them.",
    "entity.qaydConfirmedDefaults": "Kuwait, Kuwaiti Dinar, and the legal form suggested from this entity are prefilled for your confirmation when you save.",
    "entity.auditNo": "Attestation number",
    "entity.auditDate": "Attestation date",
    "entity.auditorRegistrationNumber": "Auditor registration number",
    "entity.auditorName": "Auditor name",
    "entity.accountingOffice": "Accounting office",
    "entity.legalFormOfEntity": "Legal form of entity",
    "entity.registeredOfficeAddress": "Registered office address",
    "entity.natureOfOperations": "Nature of operations",
    "entity.countryOfIncorporation": "Country of incorporation",
    "entity.presentationCurrencyDescription": "Presentation currency description",
    "entity.qaydRounding": "Qayd rounding",
    "entity.qaydActualValue": "Actual value (no rounding)",
    "entity.contactEmail": "Business contact email",
    "entity.makeDefault": "Set as active entity",
    "entity.active": "Active entity",
    "entity.created": "Entity created",
    "entity.updated": "Entity updated",
    "entity.detailsDescription": "Update the details used by your active Business workspace.",
    "entity.save": "Save business details",
    "entity.saving": "Saving…",
    "entity.fiscalYearEnd": "Fiscal year end",
    "entity.fiscalYearEndMonth": "Month",
    "entity.fiscalYearEndDay": "Day",
    "entity.xbrlEntryPoint": "XBRL entry point",
    "entity.xbrlSme": "IFRS for SMEs",
    "entity.xbrlFull": "Full IFRS",
    "entity.crFilingWarning": "Changing the CR number changes the identifier used in future XBRL filings. Previously generated filings keep their existing identifier and remain unchanged.",
    "entity.fiscalYearWarning": "This business already has journal entries. Changing its fiscal year end re-periods every financial statement.",
    "entity.currentFiscalYearEnd": "Current period end",
    "entity.newFiscalYearEnd": "New period end",
    "entity.confirmFiscalYearEnd": "I understand and want to re-period statements",
    "entity.fiscalConfirmationRequired": "Confirm the fiscal-year change before saving.",
    "entity.invalidFiscalYearEnd": "Choose a real fiscal year-end date.",
    "entity.invalidCrNumber": "Enter a CR number using digits only.",
    "entity.businessRequired": "Switch to a business account to create additional entities.",
    "entity.documents": "documents",
    "entity.transactions": "transactions",
    "entity.journalEntries": "journal entries",
    "entity.contacts": "contacts",
    "entity.delete": "Delete entity",
    "entity.deleted": "Entity deleted",
    "entity.deleteConfirm": "Type DELETE to permanently remove this unused entity.",
    "entity.adminCascadeDelete": "Admin cascade delete all records",
    "entity.adminCascadeWarning": "This permanently destroys the entity and all of its records. This action cannot be undone.",
    "entity.adminCascadeConfirm": "Type {{name}} exactly to confirm the admin deletion.",
    "entity.adminCascadeCounts": "Will destroy: {{documents}} documents, {{journals}} journal entries, {{invoices}} invoices, {{contacts}} contacts, {{contracts}} contracts, and {{filings}} filings.",
    "entity.adminCascadeDeleted": "Entity and dependent records deleted.",
    "entity.duplicateCrWarning": "This CR number already belongs to one of your entities. Create another entity with the same CR only if this is intentional.",
    "entity.confirmDuplicateCr": "Create with this CR number",
    "businessUpload.supported": "PDF, JPG, PNG, or WEBP up to 10MB",
    "businessUpload.choose": "Choose a file",
    "businessAnalytics.revenue": "Revenue",
    "businessAnalytics.expenses": "Expenses",
    "businessAnalytics.profit": "Profit",
    "invoice.title": "Invoices",
    "invoice.description": "Upload and review customer and vendor invoices for the active entity.",
    "invoice.upload": "Upload invoice",
    "invoice.uploadHint": "PDF, JPG, PNG, or WEBP up to 10MB",
    "invoice.reviewQueue": "Review queue",
    "invoice.aging": "AR / AP aging",
    "invoice.all": "All invoices",
    "invoice.receivable": "Receivable",
    "invoice.payable": "Payable",
    "invoice.direction": "Direction",
    "invoice.status": "Status",
    "invoice.counterparty": "Counterparty",
    "invoice.dateRange": "Date range",
    "invoice.needsReview": "Needs review",
    "invoice.reviewReason.general": "Review the extracted invoice details",
    "invoice.reviewReason.counterparty_unrecognised": "Counterparty not recognised",
    "invoice.reviewReason.direction_unresolved": "Direction needs confirmation",
    "invoice.reviewReason.invoice_number_unclear": "Invoice number unclear",
    "invoice.reviewReason.currency_unconfirmed": "Currency not confirmed",
    "invoice.reviewReason.arithmetic_check_failed": "Invoice arithmetic needs review",
    "invoice.reviewReason.line_caption_unclassified": "A purchase line could not be classified: review the quoted caption",
    "invoice.noInvoices": "No invoices found",
    "invoice.open": "Open",
    "invoice.paid": "Paid",
    "invoice.partiallyPaid": "Partially paid",
    "invoice.draft": "Draft",
    "invoice.void": "Void",
    "invoice.current": "0–30 days",
    "invoice.days31to60": "31–60 days",
    "invoice.days61to90": "61–90 days",
    "invoice.days90plus": "90+ days",
    "invoice.extractedValues": "Extracted values",
    "invoice.currentValue": "Current value",
    "invoice.sourceEvidence": "Source evidence",
    "invoice.settledAtSource": "Settled on source document",
    "invoice.settledAtSourceDescription": "No bank match is expected because this invoice has an explicit settlement marker on its source document.",
    "invoice.invoiceAmount": "Invoice amount",
    "invoice.kwdEquivalent": "KWD equivalent",
    "invoice.fxRate": "FX rate used",
    "invoice.currencyUnconfirmed": "Currency requires review",
    "invoice.businessRequired": "Invoices are available when business mode is enabled.",
    "invoice.health": "Entity health",
    "invoice.healthDescription": "Active entity integrity checks. Refresh to run again.",
    "invoice.refreshHealth": "Refresh health",
    "invoice.healthClear": "No issues found",
    "invoice.healthUnconfirmedCounterparties": "Counterparties needing confirmation",
    "invoice.healthNeedsReview": "Still needs review",
    "invoice.healthMissingCounterparty": "Missing counterparty",
    "invoice.healthPastDueNoPayment": "Past due with no recorded payment",
    "invoice.healthOverSettled": "Settled above invoice total",
    "invoice.healthBankChains": "Bank balance-chain failures",
    "invoice.healthUnmatchedPayments": "Payments with no invoice match",
    "invoice.healthUnbalancedJournals": "Unbalanced journal entries",
    "invoice.healthPeriodContinuity": "Opening + movement ≠ closing",
    "invoice.healthCashFlowReconciliation": "Cash flow not reconciled to a bank balance",
    "invoice.healthStuckDocuments": "Documents stuck in processing",
    "invoice.cashFlowNotComparable": "Not comparable — no bank account linked to this entity",
    "invoice.healthReviewExcluded": "Invoices excluded from statements",
    "contract.title": "Contracts",
    "contract.description": "Extract contract terms for review, then generate expected payment or revenue schedules.",
    "contract.businessRequired": "Contracts are available when business mode is enabled.",
    "contract.upload": "Upload contract",
    "contract.alerts": "Contract alerts",
    "contract.overdue": "Expected payments not received",
    "contract.expiring": "Approaching expiry or renewal",
    "contract.none": "No contracts found",
    "contract.reviewTitle": "Review contract terms",
    "contract.reviewWarning": "Contracts never post to the ledger automatically. Confirm every term before Mizano can generate a schedule.",
    "contract.confirmedTerms": "Terms to confirm",
    "contract.reviewRequired": "Review required",
    "contract.counterparty": "Counterparty",
    "contract.type": "Contract type",
    "contract.startDate": "Start date",
    "contract.endDate": "End date",
    "contract.total": "Total value",
    "contract.frequency": "Frequency",
    "contract.amountPerPeriod": "Amount per period",
    "contract.autoRenew": "Auto-renewal",
    "contract.yes": "Yes",
    "contract.no": "No",
    "contract.confirmAll": "Confirm every term & generate schedule",
    "contract.sourceEvidence": "Source evidence",
    "contract.noEvidence": "No supporting text was extracted",
    "contract.schedule": "Expected schedule",
    "contract.linkInvoice": "Link invoice",
    "contract.linkPayment": "Link payment",
    "contract.type.lease": "Lease",
    "contract.type.service": "Service",
    "contract.type.employment": "Employment",
    "contract.type.revenue": "Revenue",
    "contract.type.other": "Other",
    "contract.status.draft": "Draft",
    "contract.status.active": "Active",
    "contract.status.expired": "Expired",
    "contract.status.terminated": "Terminated",
    "contract.frequency.one_time": "One-time",
    "contract.frequency.monthly": "Monthly",
    "contract.frequency.quarterly": "Quarterly",
    "contract.frequency.annual": "Annual",
    "contract.scheduleStatus.expected": "Expected",
    "contract.scheduleStatus.invoiced": "Invoiced",
    "contract.scheduleStatus.settled": "Settled",
    "contract.scheduleStatus.overdue": "Overdue",
    "contract.evidence.counterpartyName": "Counterparty evidence",
    "contract.evidence.contractType": "Type evidence",
    "contract.evidence.startDate": "Start-date evidence",
    "contract.evidence.endDate": "End-date evidence",
    "contract.evidence.totalValue": "Total-value evidence",
    "contract.evidence.currency": "Currency evidence",
    "contract.evidence.frequency": "Frequency evidence",
    "contract.evidence.amountPerPeriod": "Per-period evidence",
    "contract.evidence.autoRenew": "Renewal evidence",
    "contract.evidence.renewalTerms": "Renewal-terms evidence",
    "reconciliation.title": "Reconciliation",
    "reconciliation.description": "Review suggested links between invoices and bank transactions. Nothing is posted until you confirm it.",
    "reconciliation.businessRequired": "Reconciliation is available when business mode is enabled.",
    "reconciliation.refresh": "Refresh suggestions",
    "reconciliation.confirmationOnly": "Suggestions never change your books by themselves. Confirm each allocation, and unmatch it at any time to restore the invoice status.",
    "reconciliation.suggestions": "Suggested matches",
    "reconciliation.loading": "Checking eligible bank transactions…",
    "reconciliation.noSuggestions": "No reviewable invoice matches found.",
    "reconciliation.invoice": "Invoice",
    "reconciliation.transaction": "Transaction",
    "reconciliation.unknownTransaction": "Unknown transaction",
    "reconciliation.amountDifference": "Difference",
    "reconciliation.amountDifferenceConfirmation": "I confirm this payment amount differs from the invoice by more than KWD 1.000.",
    "reconciliation.remaining": "Invoice remaining",
    "reconciliation.amountApplied": "Amount to apply",
    "reconciliation.reject": "Reject",
    "reconciliation.confirm": "Confirm match",
    "reconciliation.reviewConfirm": "Review & confirm",
    "reconciliation.highConfidence": "Suggested",
    "reconciliation.reviewRequired": "Review required",
    "reconciliation.suggestedOnly": "No automatic posting",
    "reconciliation.confirmedMatches": "Confirmed matches",
    "reconciliation.noConfirmedMatches": "No confirmed matches yet.",
    "reconciliation.unmatch": "Unmatch",
    "documents.title": "Documents",
    "documents.description": "Your uploaded financial documents for the active entity.",
    "documents.allTypes": "All types",
    "documents.bankStatement": "Bank statement",
    "documents.financialStatement": "Financial statement",
    "documents.entityDocument": "Entity document",
    "documents.invoice": "Invoice",
    "documents.contract": "Contract",
    "documents.status": "Status",
    "documents.uploaded": "Uploaded",
    "documents.processing": "Processing",
    "documents.parsed": "Parsed",
    "documents.failed": "Failed",
    "documents.reviewed": "Reviewed",
    "documents.empty": "No documents for this entity yet.",
    "documents.retry": "Retry upload",
    "documents.retryReady": "Ready to retry. Upload the document again to start a new parse.",
    "documents.delete": "Delete",
    "documents.deleted": "Document deleted",
    "documents.statementImport": "Statement import",
    "documents.statementPeriod": "Period",
    "documents.statementPurpose": "Purpose",
    "documents.openingBalances": "Opening balances",
    "documents.fullPeriod": "Full period",
    "documents.statementStatus": "Status",
    "documents.mapped": "Mapped",
    "documents.unmapped": "Unmapped",
    "documents.viewPostedImport": "View posted import",
    "documents.resumeReview": "Resume review ({{count}} remaining)",
    "documents.noFinancialStatements": "No financial statements yet",
    "documents.noFinancialStatementsDescription": "Upload a statement to start a resumable mapping review.",
    "counterparties.title": "Counterparties",
    "counterparties.description": "Manage the customers and vendors for the active entity.",
    "counterparties.search": "Search counterparties",
    "counterparties.create": "Create counterparty",
    "counterparties.edit": "Edit",
    "counterparties.merge": "Merge duplicates",
    "counterparties.name": "Name",
    "counterparties.nameAr": "Arabic name",
    "counterparties.type": "Type",
    "counterparties.customer": "Customer",
    "counterparties.vendor": "Vendor",
    "counterparties.both": "Customer and vendor",
    "counterparties.taxId": "Tax ID",
    "counterparties.email": "Contact email",
    "counterparties.phone": "Contact phone",
    "counterparties.notes": "Notes",
    "counterparties.save": "Save counterparty",
    "counterparties.cancel": "Cancel",
    "counterparties.aliases": "Aliases",
    "counterparties.unconfirmed": "Needs confirmation",
    "counterparties.invoiceCount": "Invoices",
    "counterparties.openBalance": "Open balance",
    "counterparties.purchased": "Purchased",
    "counterparties.sold": "Sold",
    "counterparties.ledgerDefault": "Default ledger account for future invoices",
    "counterparties.selectLedgerDefault": "Select revenue or expense account",
    "counterparties.ledgerDefaultRequired": "Select a default ledger account before confirming this counterparty.",
    "counterparties.confirmWithLedgerDefault": "Confirm and save ledger default",
    "counterparties.confirmedWithLedgerDefault": "Counterparty confirmed and ledger default saved.",
    "counterparties.confirm": "Confirm",
    "counterparties.bankNarration": "Confirmed bank narration",
    "counterparties.confirmNarration": "Confirm narration",
    "counterparties.empty": "No counterparties for this entity yet.",
    "counterparties.created": "Counterparty created",
    "counterparties.updated": "Counterparty updated",
    "counterparties.merged": "Counterparties merged",
    "counterparties.aliasSaved": "Bank narration saved as an alias",
    "counterparties.selectSource": "Duplicate to merge",
    "counterparties.selectTarget": "Keep this counterparty",

    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.smsParser": "Upload Transactions",
    "nav.transactions": "Transactions",
    "nav.incomeStatement": "Income Statement",
    "nav.analytics": "Analytics",
    "nav.budgets": "Budgets",
    "nav.export": "Export",
    "nav.setup": "Setup",
    "nav.more": "More",
    "nav.accounts": "Accounts",
    "nav.accountSettings": "Account Settings",
    "nav.upload": "Upload",
    "nav.contacts": "Contacts",

    // User menu
    "menu.lightMode": "Light Mode",
    "menu.darkMode": "Dark Mode",
    "menu.signOut": "Sign out",
    "menu.exitDemo": "Exit Demo",
    "menu.language": "العربية",

    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.cashIn": "Cash In",
    "dashboard.cashOut": "Cash Out",
    "dashboard.netCashFlow": "Net Cash Flow",
    "dashboard.latestBalance": "Latest Balance",
    "dashboard.topSpending": "Top Spending Categories",
    "dashboard.recentTransactions": "Recent Transactions",
    "dashboard.transactions": "transactions",
    "dashboard.welcome": "Welcome to Mizano!",
    "dashboard.welcomeDesc": "Get started by uploading your bank statement or SMS messages.",
    "dashboard.goToSMS": "Upload Transactions",
    "dashboard.emptyTitle": "No transactions yet",
    "dashboard.emptyDesc": "Upload a bank statement to get started and see your financial overview.",
    "dashboard.queryUnavailable": "Your dashboard could not be loaded",
    "dashboard.queryTimeout": "Your dashboard is taking too long to load",
    "dashboard.queryRetryDescription": "Check your connection and try again. Your financial data has not been changed.",
    "dashboard.retry": "Try again",
    "route.loading": "Loading",
    "route.timeoutTitle": "This page is taking too long to load",
    "route.errorTitle": "This page could not be loaded",
    "route.errorOrigin": "Origin",
    "route.retryDescription": "Check your connection and try again. Your data has not been changed.",
    "route.retry": "Try again",
    "dashboard.noSpending": "No spending data yet",
    "dashboard.topEarning": "Top Earning Categories",
    "dashboard.noEarning": "No earning data yet",
    "dashboard.noTransactions": "No transactions yet",
    "dashboard.allAccounts": "All Accounts",
    "dashboard.filterByAccount": "Filter by Account",
    "dashboard.account": "Account",
    "verification.provisional": "Provisional",
    "verification.verified": "Statement verified",
    "verification.disputed": "Amount disputed",
    "verification.disputedDescription": "The captured amount differs from the final statement settlement. Both amounts are retained for review.",
    "verification.statementSettlement": "Statement settlement: {amount}.",

    // Reconciliation
    "reconcile.title": "Balance Reconciliation",
    "reconcile.desc": "Your tracked transactions don't match your actual bank balance. Create an adjustment to close the gap.",
    "reconcile.smsBalance": "Actual Bank Balance (SMS)",
    "reconcile.calculatedBalance": "Calculated Balance",
    "reconcile.gap": "Gap",
    "reconcile.missingIncome": "Missing Income",
    "reconcile.missingExpense": "Missing Expense",
    "reconcile.balanced": "Balanced",
    "reconcile.balancedDesc": "Your tracked transactions match your bank balance.",
    "reconcile.createAdjustment": "Create Adjustment Transaction",
    "reconcile.adjustmentCreated": "Reconciliation adjustment created successfully!",
    "reconcile.startingBalance": "Starting Balance",
    "reconcile.totalCashIn": "Total Cash In",
    "reconcile.totalCashOut": "Total Cash Out",
    "reconcile.explanation": "This will create a single transaction to account for the missing amount and align your balance.",
    "reconcile.accountDesc": "This account’s own transactions are compared only with this account’s bank balance.",
    "reconcile.allAccountsDesc": "Each bank account is reconciled separately. All Accounts is a summary, not one combined balance chain.",
    "reconcile.allAccountsSummary": "Choose an account above to review or create an adjustment. An adjustment is always posted to one selected account.",
    "reconcile.differenceFound": "Difference found",
    "reconcile.notComparable": "Not comparable",
    "reconcile.notComparableDesc": "This account needs at least an opening and a latest bank balance before it can be reconciled.",
    "reconcile.accountNeedsReview": "1 account needs review",
    "reconcile.accountsNeedReview": "{{count}} accounts need review",
    "reconcile.reviewAccounts": "Review accounts",
    "reconcile.reviewBalanceStatus": "Review balance status",

    // Upload Transactions
    "sms.title": "Upload Transaction Information",
    "sms.subtitle": "Upload bank statements, paste SMS messages, or upload screenshots to extract and categorize transactions",
    "sms.registeredBanks": "Registered Bank SMS Numbers",
    "sms.registeredBanksDesc": "Add your bank SMS sender numbers for automatic detection when pasting messages",
    "sms.addBank": "Add Bank",
    "sms.editBank": "Edit Bank Sender",
    "sms.addBankTitle": "Add Bank SMS Number",
    "sms.senderNumber": "SMS Sender Number / ID",
    "sms.senderNumberPlaceholder": "e.g., NBK, +96590000000, KFH-SMS",
    "sms.senderNumberDesc": "The sender name or number that appears on your bank SMS messages",
    "sms.bankName": "Bank Name",
    "sms.bankNamePlaceholder": "e.g., National Bank of Kuwait",
    "sms.bankCode": "Bank Code (optional)",
    "sms.bankCodePlaceholder": "e.g., NBK, KFH, BOUBYAN",
    "sms.bankCodeDesc": "Short code used for tagging transactions",
    "sms.save": "Save",
    "sms.cancel": "Cancel",
    "sms.noBanks": "No bank SMS numbers registered yet",
    "sms.active": "Active",
    "sms.inactive": "Inactive",

    // SMS Input
    "sms.inputTitle": "Upload Transaction Data",
    "sms.statementUploader": "Bank Statement",
    "sms.single": "Single SMS",
    "sms.bulk": "Bulk SMS",
    "sms.sms": "SMS",
    "sms.image": "Image",
    "sms.pdf": "PDF Statement",
    "sms.fileUpload": "File Upload",
    "sms.pasteHere": "Paste your bank SMS message here...",
    "sms.pasteBulk": "Paste multiple SMS messages (separated by blank lines)...",
    "sms.pasteSms": "Paste one or more bank SMS messages (separate multiple messages with a blank line)...",
    "sms.detectedBank": "Detected bank:",
    "sms.parseAndSave": "Parse & Save",
    "sms.parsing": "Parsing...",
    "sms.reportIssue": "Report parsing issue",
    "sms.parseResult": "Parse Result",
    "sms.bulkResults": "Bulk Parse Results",
    "sms.parsed": "parsed",
    "sms.failed": "failed",
    "sms.duplicates": "duplicates",
    "sms.failedMessages": "Failed Messages:",
    "sms.couldNotParse": "Could not parse SMS",
    "sms.couldNotParseDesc": "The message format was not recognized. Try a different format or report the issue.",
    "sms.supportedFormats": "Supported SMS Formats",

    // Image upload
    "sms.imageTitle": "Upload SMS Screenshot",
    "sms.imageDesc": "Upload a screenshot of your bank SMS messages. The app will read and parse each message automatically.",
    "sms.dragDrop": "Drag & drop an image here, or click to browse",
    "sms.supportedTypes": "Supports PNG, JPG, JPEG, and PDF bank statements",
    "sms.maxSize": "Max file size: 10MB per file",
    "sms.uploadAndParse": "Upload & Parse",
    "sms.uploading": "Uploading...",
    "sms.processing": "Processing image...",
    "sms.imageResults": "Image Parse Results",
    "sms.pdfResults": "PDF Statement Results",
    "sms.pdfProcessing": "Processing PDF statement...",
    "sms.pdfReconcileTitle": "Checking a completed statement import",
    "sms.pdfReconcileDescription": "This browser request ended before the server replied. The import may still have completed safely.",
    "sms.pdfCompletedAfterReconnect": "Statement import completed: {count} transactions were added. Your dashboard has been refreshed.",
    "sms.pdfAlreadyImported": "This exact statement was already imported. No duplicate transactions were created.",
    "sms.pdfZeroRowsTitle": "No transactions were extracted",
    "sms.pdfZeroRowsDescription": "We found {expected} transaction-like rows across {pages} pages, but extracted none. The statement was not imported.",
    "sms.pdfTransportTitle": "The statement request was interrupted",
    "sms.pdfTransportDescription": "The server may still finish safely. We are checking the statement before offering a retry.",
    "sms.pdfRetry": "Retry statement import",
    "sms.pdfProtectedRecovered": "Your statement was protected against copying. Mizano safely prepared it for reading and did not keep a decrypted copy.",
    "sms.pdfPasswordTitle": "This statement needs its opening password",
    "sms.pdfPasswordDescription": "Your bank protected this PDF. Enter the password used to open the statement; it is used only for this request and is never saved.",
    "sms.pdfPasswordLabel": "Statement password",
    "sms.pdfPasswordRequired": "Enter the statement password to continue",
    "sms.pdfRefreshImports": "Check completed import",
    "sms.pdfControlPassed": "Control totals: passed",
    "sms.pdfControlUnavailable": "Control totals: unavailable",
    "sms.pdfControlForcedMismatch": "Control totals: mismatch overridden",
    "sms.pdfNotBankStatementTitle": "This file is not a bank-account statement",
    "sms.pdfNotBankStatementDescription": "It was not imported. No statement record or import claim was created.",
    "sms.pdfClassificationEvidence": "Classification evidence:",
    "sms.pdfChooseAnother": "Choose another document",
    "sms.pdfPages": "pages processed",
    "sms.pdfTransactions": "transactions extracted",
    "sms.pdfBank": "Bank:",
    "sms.pdfCustomer": "Customer:",
    "sms.pdfPeriod": "Period:",
    "sms.deleteAll": "Delete All Transactions",
    "sms.deleteAllConfirm": "Are you sure you want to delete ALL your transactions? This action cannot be undone.",
    "sms.deleteAllSuccess": "All transactions deleted successfully",
    "sms.deleting": "Deleting...",
    "sms.extractedMessages": "Extracted Messages:",
    "sms.bankDetected": "Bank detected:",

    // Transaction fields
    "field.type": "Type",
    "field.amount": "Amount",
    "field.merchant": "Merchant",
    "field.category": "Category",
    "field.date": "Date",
    "field.balance": "Balance",
    "field.bankSource": "Bank Source",
    "field.notes": "Notes",
    "field.cashIn": "Cash In",
    "field.cashOut": "Cash Out",

    // Transactions page
    "transactions.title": "Transactions",
    "transactions.subtitle": "View and manage all your transactions",
    "transactions.search": "Search transactions...",
    "transactions.allTypes": "All Types",
    "transactions.allCategories": "All Categories",
    "transactions.allBanks": "All Banks",
    "transactions.noTransactions": "No transactions found",
    "transactions.noTransactionsDesc": "Parse some SMS messages to see your transactions here.",
    "transactions.edit": "Edit",
    "transactions.delete": "Delete",
    "transactions.editTitle": "Edit Transaction",
    "transactions.saveChanges": "Save Changes",

    // Income Statement
    "income.title": "Income Statement",
    "income.subtitle": "Summary of your income and expenses",
    "income.totalIncome": "Total Cash In",
    "income.totalExpenses": "Total Cash Out",
    "income.netCashFlow": "Net Cash Flow",
    "income.startingBalance": "Starting Balance",
    "income.currentBalance": "Current Balance",
    "income.noData": "No income statement data yet",
    "income.noDataDesc": "Parse some SMS messages to generate your income statement.",

    // Analytics
    "analytics.title": "Analytics",
    "analytics.subtitle": "Spending trends and category breakdowns",
    "analytics.spendingTrend": "Spending Trend",
    "analytics.categoryBreakdown": "Category Breakdown",
    "analytics.noData": "No analytics data yet",
    "analytics.noDataDesc": "Parse some SMS messages to see your spending analytics.",

    // Budgets
    "budgets.title": "Budgets",
    "budgets.subtitle": "Set monthly spending limits per category",
    "budgets.addBudget": "Add Budget",
    "budgets.category": "Category",
    "budgets.limit": "Limit",
    "budgets.spent": "Spent",
    "budgets.remaining": "Remaining",
    "budgets.period": "Period",
    "budgets.monthly": "Monthly",
    "budgets.weekly": "Weekly",
    "budgets.noBudgets": "No budgets set yet",
    "budgets.noBudgetsDesc": "Set budget limits to track your spending by category.",
    "budgets.alerts": "Budget Alerts",
    "budgets.overBudget": "Over budget!",
    "budgets.createBudget": "Create Budget",

    // Export
    "export.title": "Financial Statements",
    "export.subtitle": "Professional income statements and reports",
    "export.csv": "Export CSV",
    "export.pdf": "Export PDF",
    "export.noData": "No data to export",
    "export.noDataDesc": "Upload some transactions first to generate financial statements.",
    "export.monthly": "Monthly",
    "export.quarterly": "Quarterly",
    "export.yearly": "Yearly",
    "export.statementOfIncome": "Statement of Income",
    "export.periodEnded": "Period Ended",
    "export.revenue": "Cash In",
    "export.totalRevenue": "Total Cash In",
    "export.expenses": "Cash Out",
    "export.totalExpenses": "Total Cash Out",
    "export.netIncome": "Net Cash Flow",
    "export.cashFlowSummary": "Cash Flow Summary",
    "export.downloadPdf": "Download PDF",
    "export.downloadCsv": "Download CSV",
    "export.workspaceData": "Export this workspace",
    "export.workspaceExportReady": "Workspace data export is ready",
    "export.workspaceExportUnavailable": "Workspace data could not be prepared. Please try the export again.",
    "export.workspaceScopeHint": "This download contains only the active workspace. Use Account Settings to export all Personal and Business data together.",
    "export.transactions": "transactions",
    "export.q1": "Q1",
    "export.q2": "Q2",
    "export.q3": "Q3",
    "export.q4": "Q4",
    "export.currentPeriod": "Current Period",
    "export.kwdThousands": "KWD",
    "export.unaudited": "Unaudited",

    // Setup
    "setup.title": "Setup",
    "setup.step1Title": "Your Profile",
    "setup.step1Desc": "Tell us a bit about yourself",
    "setup.step2Title": "Bank SMS Numbers",
    "setup.step2Desc": "Configure your bank SMS sender numbers",
    "setup.name": "Full Name",
    "setup.phone": "Phone Number",
    "setup.next": "Next",
    "setup.back": "Back",
    "setup.finish": "Finish Setup",
    "setup.skip": "Skip for now",

    // Common
    "common.loading": "Loading...",
    "common.retry": "Retry",
    "common.error": "Error",
    "common.success": "Success",
    "common.confirm": "Confirm",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.search": "Search",
    "common.filter": "Filter",
    "common.noData": "No data",
    "common.yes": "Yes",
    "common.no": "No",
    "invoice.needsAmending": "Needs amending",
    "invoice.matching": "Matching",
    "common.viewAll": "View All",

    // Success popup
    "success.uploadComplete": "Upload Complete!",
    "success.singleParsed": "Transaction parsed and saved successfully.",
    "success.bulkParsed": "{parsed} transactions parsed. {failed} failed. {duplicates} duplicates.",
    "success.imageParsed": "{parsed} transactions extracted from image. {failed} failed. {duplicates} duplicates.",
    "success.pdfParsed": "{parsed} transactions extracted from {pages} pages. {duplicates} duplicates.",
    "success.pdfAllDuplicates": "Extracted {total} transactions from {pages} pages. All are duplicates (already imported).",
    "success.viewTransactions": "View Transactions",
    "success.uploadMore": "Upload More",
    "success.done": "Done",
    "success.deleteComplete": "All Transactions Deleted",
    "success.deleteDesc": "All your transaction data has been permanently removed.",
  },

  ar: {
    // App
    "app.title": "ميزانو",
    "app.subtitle": "ادخار ذكي، تتبع واضح",
    "app.signIn": "تسجيل الدخول",
    "app.signInDesc": "اقرأ كشوف حساباتك البنكية، وتتبع كل دينار. سجّل الدخول للبدء.",

    // نوع الحساب وقائمة انتظار الأعمال
    "accountType.eyebrow": "حسابك في ميزانو",
    "accountType.title": "اختر طريقة استخدامك لميزانو",
    "accountType.description": "اختر نوع الحساب الذي يناسب ما تريد تتبعه. يمكنك تغيير هذا لاحقاً من إعدادات الحساب.",
    "accountType.individualTitle": "فردي",
    "accountType.individualDescription": "تتبّع أموالك الخاصة: التدفق النقدي والميزانيات والإنفاق من كشوف حساباتك البنكية.",
    "accountType.businessTitle": "أعمال",
    "accountType.businessDescription": "أدر دفاتر شركتك. كل ما في الحساب الفردي، إضافة إلى الفواتير والذمم المدينة والدائنة والقوائم المالية.",
    "workspace.choose": "اختر مساحة العمل",
    "workspace.individual": "فردي",
    "workspace.business": "أعمال",
    "workspace.individualDescription": "الخدمات المصرفية الشخصية والميزانيات وتتبع التدفق النقدي.",
    "workspace.businessDescription": "مستندات الكيان والفواتير والمطابقة والقوائم المالية.",
    "workspace.inDevelopment": "قيد التطوير",
    "workspace.continueIndividual": "متابعة تسجيل الدخول للفردي",
    "workspace.continueBusiness": "متابعة تسجيل الدخول للأعمال",
    "workspace.switch": "تبديل مساحة العمل",
    "workspace.unavailable": "مساحة عمل الأعمال غير متاحة. اختر إعداد الأعمال أو انضم إلى قائمة الانتظار.",
    "accountType.continueTitle": "المتابعة كحساب فردي",
    "accountType.continueDescription": "يمكنك تغيير هذا الاختيار لاحقاً من إعدادات الحساب. لا تُحذف بياناتك أبداً عند تغيير نوع الحساب.",
    "accountType.businessDetails": "بيانات الأعمال",
    "accountType.businessDetailsDescription": "ستصبح هذه البيانات كيان أعمالك في مرحلة لاحقة.",
    "accountType.companyName": "اسم الشركة",
    "accountType.companyNameOptional": "اسم الشركة (اختياري)",
    "accountType.companyNameAr": "اسم الشركة بالعربية (اختياري)",
    "accountType.crNumber": "رقم السجل التجاري (اختياري)",
    "accountType.fiscalMonth": "شهر نهاية السنة المالية",
    "accountType.fiscalDay": "يوم نهاية السنة المالية",
    "accountType.email": "البريد الإلكتروني",
    "accountType.waitlistTitle": "حسابات الأعمال قريباً",
    "accountType.waitlistDescription": "انضم إلى قائمة الانتظار وسنراسلك بالبريد الإلكتروني عند جاهزية حسابات الأعمال.",
    "accountType.joinWaitlist": "انضم إلى قائمة الانتظار",
    "accountType.joinWaitlistDescription": "سيبقى حسابك الشخصي في ميزانو متاحاً الآن. نحتاج فقط إلى بريدك لإبقائك على اطلاع.",
    "accountType.continue": "متابعة",
    "accountType.back": "رجوع",
    "accountType.backHome": "العودة إلى الصفحة الرئيسية لميزانو",
    "accountType.lightMode": "التبديل إلى الوضع الفاتح",
    "accountType.darkMode": "التبديل إلى الوضع الداكن",
    "accountType.toggleLanguage": "تبديل اللغة",
    "accountType.submitError": "تعذر حفظ نوع حسابك. يرجى المحاولة مرة أخرى.",
    "settings.accountType": "نوع الحساب",
    "settings.currentType": "النوع الحالي",
    "settings.accountTypeDescription": "اختر ما إذا كان ميزانو لمالك الشخصية أو لشركتك. التبديل إلى فردي لا يحذف بيانات الأعمال أبداً.",
    "settings.changeAccountType": "تغيير نوع الحساب",
    "settings.buildInfo": "معلومات الإصدار",
    "settings.buildGit": "Git",
    "settings.buildCheckpoint": "نقطة الحفظ",
    "settings.buildTime": "وقت البناء",
    "settings.tabsLabel": "أقسام إعدادات الحساب",
    "settings.tab.profile": "الملف الشخصي",
    "settings.tab.balance": "حالة الأرصدة",
    "settings.tab.entities": "الكيانات",
    "settings.tab.fx": "أسعار الصرف",
    "settings.tab.privacy": "البيانات والخصوصية",
    "account.exportAllTitle": "تصدير جميع بياناتي (مساحتا العمل)",
    "accounts.balanceStatusLink": "عرض حالة الأرصدة في إعدادات الحساب",
    "account.exportAllDescription": "نزّل نسخة خصوصية كاملة من كل البيانات المرتبطة بحسابك، بما في ذلك سجلات مساحتي العمل الشخصية والتجارية.",
    "account.exportAllJson": "تصدير جميع البيانات بصيغة JSON",
    "account.exportAllCsv": "تصدير جميع المعاملات بصيغة CSV",
    "account.exportAllHint": "استخدم صفحة التصدير لمساحة عمل واحدة فقط. يتضمن هذا التصدير من إعدادات الحساب مساحتي العمل وملف حسابك.",
    "businessDataClear.title": "مسح بيانات الأعمال",
    "businessDataClear.description": "إزالة جميع بيانات مساحة عمل الأعمال هذه مع الاحتفاظ بملف المنشأة ودليل الحسابات.",
    "businessDataClear.start": "مسح بيانات الأعمال",
    "businessDataClear.warning": "هذا الإجراء نهائي ولا يمكن التراجع عنه.",
    "businessDataClear.previewTitle": "بيانات الأعمال المجدولة للحذف النهائي",
    "businessDataClear.typeExact": "اكتب اسم المنشأة هذا كما هو تماماً للتأكيد:",
    "businessDataClear.confirmInput": "تأكيد اسم المنشأة",
    "businessDataClear.cancel": "إلغاء",
    "businessDataClear.confirm": "مسح بيانات الأعمال نهائياً",
    "businessDataClear.clearing": "جارٍ مسح بيانات الأعمال…",
    "businessDataClear.retained": "سيتم الاحتفاظ بملف المنشأة ودليل الحسابات المُعد مسبقاً.",
    "businessDataClear.success": "تم مسح بيانات الأعمال مع الاحتفاظ بملف المنشأة ودليل الحسابات.",
    "businessDataClear.storageIncomplete": "تم مسح سجلات الأعمال، لكن تعذر حذف ملف مخزن واحد أو أكثر. تواصل مع الدعم قبل إعادة استخدام أسماء هذه الملفات.",
    "businessDataClear.failed": "تعذر مسح بيانات الأعمال. لم تُتخذ أي خطوة إضافية من هذه الشاشة.",
    "businessDataClear.nameMismatch": "اكتب اسم المنشأة تماماً لتأكيد هذا الإجراء النهائي.",
    "businessDataClear.documents": "المستندات",
    "businessDataClear.transactions": "المعاملات",
    "businessDataClear.journalEntries": "قيود اليومية",
    "businessDataClear.journalLines": "بنود اليومية",
    "businessDataClear.counterparties": "الأطراف المقابلة",
    "businessDataClear.counterpartyAliases": "الأسماء البديلة للأطراف المقابلة",
    "businessDataClear.invoices": "الفواتير",
    "businessDataClear.invoiceLines": "بنود الفواتير",
    "businessDataClear.invoiceMatches": "مطابقات الفواتير",
    "businessDataClear.contracts": "العقود",
    "businessDataClear.contractSchedules": "جداول العقود",
    "businessDataClear.inventoryCounts": "جرد المخزون",
    "businessDataClear.financialStatementImports": "استيرادات القوائم المالية",
    "businessDataClear.openingBalanceDrafts": "مسودات الأرصدة الافتتاحية",
    "businessDataClear.xbrlFilings": "إيداعات XBRL",
    "businessDataClear.services": "الخدمات",
    "businessDataClear.serviceComponents": "مكونات الخدمات",
    "businessDataClear.bankAccounts": "الحسابات البنكية للأعمال",
    "businessDataClear.fileImports": "استيرادات ملفات البنك",
    "businessDataClear.pdfImportAudits": "تدقيق استيراد PDF",
    "businessDataClear.budgets": "الميزانيات",
    "businessDataClear.parsingErrors": "أخطاء التحليل",
    "businessDataClear.bankSenders": "مرسلو البنك",
    "businessDataClear.ledgerMappings": "تعيينات دفتر الأستاذ",
    "businessDataClear.storageFiles": "الملفات المخزنة",
    "qayd.title": "مصنف قيد",
    "qayd.description": "أنشئ مصنف قيد الرسمي الإصدار 1.7 الممكّن بالماكرو للرفع في البوابة. يلزم الرقم المدني للجهة وبيانات التوثيق المؤكدة.",
    "qayd.export": "تنزيل مصنف قيد",
    "qayd.workbookDownloadTitle": "مصنف قيد (.xlsm)",
    "qayd.portalInstruction": "الصيغة التي تقبلها بوابة وزارة التجارة والصناعة. افتح الملف في Excel، واضغط التحقق من البيانات المالية المقيدة، ثم احفظه وارفعه.",
    "qayd.exporting": "جارٍ إعداد مصنف قيد…",
    "qayd.acceptedFormat": "مصنف قيد المحفوظ بالماكرو هو صيغة التقديم في البوابة.",
    "qayd.templateVersion": "إصدار القالب",
    "qayd.metadataRequired": "أكمل الرقم المدني للجهة وبيانات إيداع قيد في إعدادات الكيان قبل التصدير.",
    "qayd.metadataRequiredTitle": "بيانات إيداع قيد مطلوبة",
    "qayd.metadataRequiredDescription": "يعرّف مصنف قيد الجهة ويسجل بيانات التوثيق التي أكدها مراقب الحسابات. أكمل الحقول التالية مرة واحدة في إعدادات الكيان قبل التصدير.",
    "qayd.completeMetadata": "إكمال بيانات إيداع قيد",
    "qayd.statementRequired": "يجب أن تتطابق القوائم المالية قبل إنشاء تصدير للإيداع.",
    "qayd.templateCorrupt": "قالب قيد المحلي الموثق غير متاح أو تالف. لم يتم إنشاء أي مصنف.",
    "qayd.generated": "تم إنشاء مصنف قيد. ارفع الملف الذي تم تنزيله إلى بوابة قيد.",
    "qayd.bundleTitle": "حزمة إيداع قيد الكاملة (.zip)",
    "qayd.bundleDescription": "تنزيل واحد يضم مصنف البوابة وملفي مراجعة XBRL وتعليمات التقديم بالعربية والإنجليزية.",
    "qayd.bundleExporting": "جارٍ إعداد حزمة الإيداع…",
    "qayd.bundleGenerated": "تم إنشاء حزمة إيداع قيد.",
    "qayd.individualDownloads": "الملفات الفردية",
    "qayd.exportTimedOut": "يستغرق إنشاء قيد أكثر من دقيقة. راجع سجل الإيداعات قبل إعادة المحاولة.",
    "qayd.completedIn": "اكتمل تصدير قيد خلال {seconds} ثوانٍ.",
    "qayd.attestationOptional": "بيانات توثيق مراقب الحسابات اختيارية. اتركها فارغة ما لم تكن مدعومة بتقرير مراقب الحسابات؛ لا يقوم ميزانو بإنشائها أو استنتاجها.",
    "qayd.draftXbrl": "تستخدم ملفات مراجعة XBRL أسماء عناصر قيد رسمية موثقة، لكن تعريفات مساحات الأسماء فيها عناصر نائبة غير موثقة. تظل مسودات للمراجعة؛ استخدم مصنف قيد للرفع إلى البوابة.",
    "qayd.guideLink": "كيفية الإيداع عبر قيد",
    "qayd.guideEyebrow": "دليل إيداع قيد",
    "qayd.guideTitle": "إعداد إيداع قيد والتحقق منه",
    "qayd.guideDescription": "استخدم قائمة التحقق هذه بعد تنزيل حزمة إيداع قيد. يجب فتح مصنف البوابة والتحقق منه في تطبيق Excel لسطح المكتب قبل رفعه.",
    "qayd.guideBack": "العودة إلى تصديرات قيد",
    "qayd.guideWindowsTitle": "يلزم إجراء أمان في Windows",
    "qayd.guideWindowsDescription": "يحظر Windows الماكرو في الملفات المنزلة من الإنترنت حتى يتم إلغاء حظر المصنف.",
    "qayd.guideWindowsNotice": "ينطبق ذلك حتى عند توقيع المصنف بصورة صحيحة. ألغِ حظر ملف .xlsm بعد استخراجه وقبل فتحه في Excel، وإلا فلن يعمل ماكرو التحقق.",
    "qayd.guideStepsTitle": "إجراءات الإيداع",
    "qayd.guideStepsDescription": "أكمل جميع الخطوات بالترتيب قبل الرفع إلى بوابة قيد في وزارة التجارة والصناعة.",
    "qayd.guideStep1Title": "استخراج الحزمة",
    "qayd.guideStep1Description": "استخرج ملف ZIP إلى مجلد. لا تفتح المصنف بينما لا يزال داخل ملف ZIP.",
    "qayd.guideStep2Title": "لنظام Windows فقط: إلغاء حظر المصنف",
    "qayd.guideStep2Description": "انقر بزر الفأرة الأيمن على qayd_v1.7.xlsm، واختر خصائص، ثم فعّل إلغاء الحظر في أسفل علامة التبويب عام، ثم اختر تطبيق وموافق.",
    "qayd.guideStep3Title": "استخدم Excel على جهاز كمبيوتر",
    "qayd.guideStep3Description": "افتح المصنف في تطبيق Microsoft Excel لسطح المكتب على جهاز كمبيوتر. لا يستطيع Excel Online أو الهاتف تشغيل ماكرو التحقق.",
    "qayd.guideStep4Title": "تمكين محتوى الماكرو",
    "qayd.guideStep4Description": "إذا ظهر شريط أمان أصفر في Excel، اختر تمكين المحتوى.",
    "qayd.guideStep5Title": "تشغيل تحقق قيد",
    "qayd.guideStep5Description": "في ورقة البيانات الاساسية، اختر التحقق من البيانات المالية المقيدة.",
    "qayd.guideStep6Title": "مراجعة الأخطاء والتحذيرات",
    "qayd.guideStep6Description": "افتح علامة تبويب الاخطاء و التحذيرات وعالج كل خطأ معروض قبل الإيداع.",
    "qayd.guideStep7Title": "الحفظ والرفع",
    "qayd.guideStep7Description": "احفظ المصنف بعد التحقق، ثم ارفع ملف .xlsm المحفوظ إلى بوابة قيد في وزارة التجارة والصناعة.",
    "qayd.guideUnblockImageAlt": "خصائص ملف Windows مع تمييز خانة إلغاء الحظر",
    "qayd.guideEnableContentImageAlt": "تحذير أمان Excel مع تمييز تمكين المحتوى",
    "qayd.guideValidateImageAlt": "زر التحقق في قيد على ورقة البيانات الأساسية مع تمييزه",
    "qayd.guideAttestationTitle": "توثيق مراقب الحسابات قبل الرفع",
    "qayd.guideAttestationDescription": "يترك المصنف حقول تقرير مراقب الحسابات غير المدعومة فارغة بصورة مقصودة.",
    "qayd.guideAttestationNotice": "أكمل رقم التوثيق وتاريخه ورقم قيد مراقب الحسابات واسمه واسم المكتب من تقرير مراقب الحسابات فقط. لا ينشئ ميزانو هذه القيم أو يخمنها؛ سيشير مدقق قيد إلى نقص بيانات التوثيق المطلوبة.",
    "qayd.guideReadyTitle": "هل أنت مستعد لإعداد الإيداع؟",
    "qayd.guideReadyDescription": "ارجع إلى تصديرات قيد لتنزيل حزمة الإيداع.",
    "qayd.disclosuresTitle": "إيضاحات قيد",
    "qayd.disclosuresDescription": "تُحدد إفصاحات المنشأة السردية من النموذج العربي الرسمي وفقاً لوقائع قيد في فترة التقرير المحددة.",
    "qayd.disclosuresNonBlocking": "لا تمنع الإيضاحات الناقصة تنزيل المصنف، لكن مدقق قيد سيعرض كل إفصاح مطلوب بقي فارغاً.",
    "qayd.disclosuresRequired": "مطلوب لهذه الفترة",
    "qayd.disclosuresComplete": "تم تأكيد جميع الإفصاحات المطلوبة لهذه الفترة صراحةً.",
    "qayd.disclosuresUnfilled": "توجد إفصاحات مطلوبة غير مكتملة أو غير مؤكدة.",
    "qayd.disclosuresManage": "إدارة إيضاحات القوائم",
    "qayd.disclosuresEntityBaseline": "إفصاح منشأة قابل لإعادة الاستخدام",
    "qayd.disclosuresPeriodOverride": "تجاوز خاص بالفترة",
    "qayd.disclosuresDraft": "مسودة — لا تُضمّن في المصنف",
    "qayd.disclosuresConfirmed": "مؤكد — يُضمّن في المصنف",
    "qayd.disclosuresUnfilledState": "فارغ",
    "qayd.disclosuresSuggestion": "مسودة مقترحة معيارية",
    "qayd.disclosuresUseSuggestion": "استخدام الاقتراح القابل للتعديل",
    "qayd.disclosuresSaveDraft": "حفظ المسودة",
    "qayd.disclosuresConfirm": "تأكيد للإيداع",
    "qayd.disclosuresConfirmAcknowledgement": "أؤكد أن هذا هو إفصاح المنشأة الذي تمت مراجعته للنطاق المحدد، ويمكن تضمينه في مصنف قيد.",
    "qayd.disclosuresTextAr": "نص الإفصاح العربي",
    "qayd.disclosuresTextEn": "النص الإنجليزي المرجعي",
    "qayd.disclosuresTextHint": "يُكتب النص العربي في نموذج قيد العربي الرسمي. ويُحتفظ بالنص الإنجليزي كمرجع تمت مراجعته للمنشأة.",
    "qayd.disclosuresDraftSaved": "تم حفظ مسودة الإفصاح. راجعها وأكدها صراحةً قبل تضمينها في المصنف.",
    "qayd.disclosuresConfirmedSaved": "تم تأكيد الإفصاح للإيداع.",
    "qayd.disclosuresError": "تعذر حفظ الإفصاح. راجع النص وحاول مرة أخرى.",
    "qayd.disclosuresArabicOnly": "تنشر وزارة التجارة والصناعة نموذج قيد الرسمي باللغة العربية فقط. القوائم الإنجليزية من تصدير Excel تقارير مرجعية ولا يمكن تقديمها إلى قيد.",
    "qayd.disclosuresPeriod": "نهاية فترة التقرير",
    "qayd.disclosuresScope": "نطاق الإفصاح",
    "qayd.disclosuresShowAll": "إظهار جميع حقول الإيضاحات الرسمية",
    "qayd.disclosuresOnlyRequired": "إظهار الحقول المطلوبة فقط",
    "qayd.disclosuresNoRequirements": "غير مطلوب لفترة التقرير المحددة",
    "admin.businessWaitlistTitle": "قائمة انتظار الأعمال",
    "admin.businessWaitlistDescription": "الأشخاص الذين طلبوا إشعارهم عند إتاحة حسابات الأعمال.",
    "admin.businessWaitlistTotal": "إجمالي إدخالات قائمة الانتظار",
    "admin.businessWaitlistEmpty": "لا توجد إدخالات في قائمة انتظار الأعمال بعد.",
    "admin.businessWaitlistDenied": "هذه الصفحة الإدارية غير متاحة لحسابك.",
    "admin.noCompanyName": "لم يُقدَّم اسم شركة",
    "admin.businessAccessTitle": "تجاوزات صلاحية الأعمال",
    "admin.businessAccessDescription": "منح أو إزالة صلاحية مساحة عمل الأعمال لمستخدم محدد. يتم تدقيق كل تغيير.",
    "admin.businessAccessEnabled": "تم تمكين صلاحية الأعمال",
    "admin.businessAccessEnable": "تمكين صلاحية الأعمال",
    "admin.businessAccessEmpty": "لا يوجد مستخدمون.",
    "admin.noEmail": "لا يوجد بريد إلكتروني",
    "admin.fxRatesTitle": "أسعار الصرف",
    "admin.fxRatesDescription": "أدر أسعار الصرف العالمية بالدينار الكويتي لكل وحدة حسب تاريخ السريان. هذه الأسعار متاحة لجميع المستخدمين.",
    "admin.fxRatesDenied": "هذه الصفحة لإدارة أسعار الصرف غير متاحة لحسابك.",
    "admin.fxRatesAdd": "إضافة سعر",
    "admin.fxRatesCurrency": "العملة",
    "admin.fxRatesRate": "د.ك لكل وحدة",
    "admin.fxRatesEffective": "تاريخ السريان",
    "admin.fxRatesSource": "المصدر",
    "admin.fxRatesCurrent": "سجل الأسعار",
    "admin.fxRatesEmpty": "لم يتم العثور على أسعار صرف.",
    "fx.convertedAt": "تم التحويل بسعر",
    "fx.on": "في",
    "ledger.title": "دفتر الأستاذ",
    "ledger.description": "محاسبة القيد المزدوج لكيان عملك. يجب أن تتوازن القيود دائماً بدقة.",
    "ledger.openingTitle": "الأرصدة الافتتاحية",
    "ledger.openingRequired": "الأرصدة الافتتاحية مطلوبة قبل أن يتمكن ميزانو من إعداد القوائم المالية للأعمال.",
    "ledger.openingReady": "تم تسجيل الأرصدة الافتتاحية. يمكن للقوائم المالية استخدام دفتر الأستاذ.",
    "ledger.account": "حساب دفتر الأستاذ",
    "ledger.selectAccount": "اختر الحساب",
    "ledger.debit": "مدين",
    "ledger.credit": "دائن",
    "ledger.debits": "إجمالي المدين",
    "ledger.credits": "إجمالي الدائن",
    "ledger.remove": "إزالة",
    "ledger.addLine": "إضافة سطر",
    "ledger.saveOpening": "حفظ الأرصدة الافتتاحية",
    "ledger.trialBalance": "ميزان المراجعة",
    "ledger.trialBalanceDescription": "جميع الحسابات النشطة ومدينها ودائنها وصافي حركتها للسنة المالية المختارة.",
    "ledger.trialBalanceBlocked": "أضف أرصدة افتتاحية متوازنة أولاً. لن يُنشئ ميزانو ميزانية عمومية بدونها.",
    "ledger.netMovement": "صافي الحركة",
    "ledger.balanced": "متوازن",
    "ledger.unbalanced": "غير متوازن",
    "ledger.adjustmentsTitle": "تسويات المطابقة",
    "ledger.adjustmentsDescription": "هذه الفروقات لا تُرحل تلقائياً. حوّل واحدة إلى قيد متوازن أو اتركها خارج دفتر الأستاذ.",
    "ledger.convertAdjustment": "تحويل إلى قيد",
    "ledger.createEntry": "إنشاء قيد متوازن",
    "ledger.noAdjustments": "لا توجد تسويات مطابقة تحتاج مراجعة.",
    "ledger.unknown": "غير معروف",
    "ledger.openingWizard": "رصيد افتتاحي قائم على المستندات",
    "ledger.openingWizardDescription": "اختر المسار المناسب للكيان، وأرفق المستندات الداعمة، ثم راجع وأكد قيد افتتاحي متوازن.",
    "ledger.openingFiscalYear": "السنة المالية",
    "ledger.openingSourceDocuments": "المستندات المصدرية",
    "ledger.openingSourceDocumentsDescription": "يمكن فقط للمستندات التابعة للكيان النشط أن تدعم هذا الرصيد الافتتاحي.",
    "ledger.openingNoDocuments": "لا توجد مستندات للكيان بعد. ارفع كشف حساب بنكي أو مستند كيان أولاً.",
    "ledger.openingNoSpecificSource": "لا يوجد مصدر محدد للسطر",
    "ledger.openingSourceDocument": "المستند الداعم",
    "ledger.openingSide": "جانب القيد",
    "ledger.openingRoute.prior_statements": "قوائم مالية سابقة",
    "ledger.openingRoute.small_company": "مستندات الكيان والنقد البنكي",
    "ledger.openingRoute.new_company": "شركة حديثة التأسيس",
    "ledger.openingRouteDescription.prior_statements": "أدخل الأرصدة المراجعة من القوائم المالية السابقة.",
    "ledger.openingRouteDescription.small_company": "اشتق الأرباح المحتجزة من النقد ورأس المال وبنود الافتتاح الاختيارية المؤكدة.",
    "ledger.openingRouteDescription.new_company": "أكد أن النقد المدفوع أولاً يساوي رأس مال المالك.",
    "ledger.openingPriorLines": "أرصدة القوائم السابقة",
    "ledger.openingPriorLinesDescription": "أدخل رصيد كل حساب من القائمة السابقة المختارة، ثم تحقق من إجمالي المدين والدائن.",
    "ledger.openingSmallCompanyInputs": "بنود الافتتاح المؤكدة",
    "ledger.openingSmallCompanyDescription": "تُشتق الأرباح المحتجزة من وضع الافتتاح المؤكد وتبقى ظاهرة للمراجعة.",
    "ledger.openingNewCompanyInputs": "التمويل الأولي",
    "ledger.openingNewCompanyDescription": "تبدأ الشركة الجديدة بلا أرباح محتجزة. يجب أن يساوي النقد المدفوع رأس المال بدقة.",
    "ledger.capitalPaidDate": "تاريخ دفع رأس المال",
    "ledger.incorporationDate": "تاريخ التأسيس (عند التأكيد)",
    "ledger.capitalPaidDateHelp": "أدخل تاريخ استلام رأس المال المدفوع فعلياً. لا يُفترض أن يكون تاريخ التأسيس أو الترخيص هو تاريخ الدفع.",
    "ledger.openingField.cash": "النقد البنكي",
    "ledger.openingField.capital": "رأس المال المدفوع",
    "ledger.openingField.receivables": "الذمم المدينة",
    "ledger.openingField.payables": "الذمم الدائنة",
    "ledger.openingField.fixedAssets": "الأصول الثابتة",
    "ledger.openingField.accumulatedDepreciation": "مجمع الإهلاك",
    "ledger.openingField.loans": "القروض",
    "ledger.openingField.cashPaidIn": "النقد المدفوع",
    "ledger.openingDerivedRetainedEarnings": "الأرباح المحتجزة المشتقة",
    "ledger.openingNewCompanyUnbalanced": "يجب أن يساوي النقد المدفوع رأس مال المالك حتى 0.001 د.ك.",
    "ledger.openingNewCompanyRetainedEarnings": "ستبدأ الأرباح المحتجزة عند 0.000 د.ك.",
    "ledger.openingCreateDraft": "إنشاء مسودة للمراجعة",
    "ledger.openingDrafts": "مسودات الأرصدة الافتتاحية",
    "ledger.openingDraftsDescription": "تبقى المسودات للمراجعة فقط حتى تتحقق وتؤكد سطورها المتوازنة.",
    "ledger.openingNoDrafts": "لا توجد مسودات أرصدة افتتاحية بعد.",
    "ledger.openingReviewDraft": "مراجعة مسودة الافتتاح",
    "ledger.openingReviewDescription": "طابق كل سطر بالمستندات المصدرية قبل الترحيل. ينشئ التأكيد قيد الافتتاح؛ وتستخدم التسوية قيد عكس.",
    "ledger.openingDerived": "مشتق تلقائياً من مدخلاتك المؤكدة",
    "ledger.openingVerification": "تحققت من هذه السطور مقابل المستندات المصدرية المذكورة.",
    "ledger.openingUploadSource": "رفع مستند مصدر",
    "ledger.openingExtractSources": "استخراج وربط المصادر",
    "ledger.openingExtractionFailed": "تعذر استخراج المستند المصدر. راجعه يدوياً.",
    "ledger.openingDerivedUnverified": "تظهر الأرباح المحتجزة المشتقة في قائمة المركز المالي إلى أن تؤكد الرصيد الافتتاحي.",
    "ledger.openingMarkVerified": "تأكيد الرصيد الافتتاحي",
    "ledger.openingConfirm": "تأكيد وترحيل رصيد الافتتاح",
    "ledger.openingPostedEntry": "قيد الافتتاح رقم #{id} نشط حالياً.",
    "ledger.openingReverse": "عكس قيد الافتتاح",
    "businessDashboard.eyebrow": "مساحة العمل للأعمال",
    "businessDashboard.title": "لوحة الأعمال",
    "businessDashboard.cashPosition": "وضع النقد",
    "businessDashboard.cashOnHand": "النقد المتاح",
    "businessDashboard.accountCount": "{count} حسابات بنكية",
    "businessDashboard.owedToYou": "مستحق لك",
    "businessDashboard.overdueDetail": "متأخر: {amount}",
    "businessDashboard.youOwe": "مستحق عليك",
    "businessDashboard.dueNextSeven": "مستحق خلال 7 أيام: {amount}",
    "businessDashboard.netPosition": "صافي المركز",
    "businessDashboard.netPositionCaption": "النقد + الذمم المدينة − الذمم الدائنة",
    "businessDashboard.reconciliation": "حالة المطابقة",
    "businessDashboard.booksTie": "الدفاتر مطابقة للبنك حتى {date} — لا فروقات غير مفسرة.",
    "businessDashboard.booksDiffer": "توجد فروق عن أرصدة البنك المرتبطة بقيمة {amount}.",
    "businessDashboard.notComparable": "غير مطابق — لا يوجد حساب بنكي مرتبط بهذا الكيان.",
    "businessDashboard.viewAccount": "عرض الحساب",
    "businessDashboard.receivablesAging": "أعمار الذمم المدينة",
    "businessDashboard.largestOverdue": "أكبر مبلغ متأخر: {name} · {amount}",
    "businessDashboard.unknownCounterparty": "طرف مقابل غير محدد",
    "businessDashboard.needsYou": "يتطلب منك",
    "businessDashboard.nothingToDo": "لا شيء يتطلب انتباهك.",
    "businessDashboard.openingBalances": "لم يتم إعداد أرصدة الافتتاح",
    "businessDashboard.suggestedMatches": "{count} مدفوعات تطابق الفواتير",
    "businessDashboard.invoicesReview": "{count} فواتير تحتاج مراجعة",
    "businessDashboard.counterpartiesConfirm": "{count} أطراف مقابلة تحتاج تأكيداً",
    "businessDashboard.contractsDue": "{count} عقود تجدد أو تنتهي خلال 30 يوماً",
    "businessDashboard.bankChains": "{count} حسابات بنكية لها سلسلة أرصدة غير مطابقة",
    "businessDashboard.performanceUnavailable": "متاح بعد إعداد أرصدة الافتتاح.",
    "businessDashboard.revenue": "الإيرادات",
    "businessDashboard.expenses": "المصروفات",
    "businessDashboard.profit": "الربح",
    "businessDashboard.noPriorPeriod": "لا توجد مقارنة للفترة السابقة",
    "businessDashboard.priorChange": "الفترة السابقة: {percent}",
    "businessDashboard.profitMargin": "هامش الربح: {percent}",
    "businessDashboard.noLedgerActivity": "لا توجد حركة دفتر أستاذ في هذه الفترة.",
    "businessDashboard.new": "جديد",
    "businessDashboard.performance": "أداء دفتر الأستاذ",
    "businessUpload.title": "رفع مستند أعمال",
    "businessUpload.description": "أفلت ملف المصدر. يحدد ميزانو نوعه ثم تؤكده قبل التحليل.",
    "businessUpload.drop": "أفلت ملف PDF أو صورة هنا، أو اختر ملفاً",
    "businessUpload.detectedType": "نوع المستند المحدد",
    "businessUpload.confirmBeforeParse": "أكد النوع أو صححه قبل أن يبدأ ميزانو مسار المراجعة المناسب.",
    "businessUpload.continue": "تأكيد ومتابعة",
    "businessUpload.detecting": "جارٍ تحديد نوع المستند…",
    "businessUpload.detectionFailed": "تعذر تحديد المستند. اختر نوعه للمتابعة.",
    "businessUpload.recentStatementReviews": "مراجعات القوائم المالية الأخيرة",
    "businessUpload.statementImport": "استيراد القوائم المالية",
    "businessUpload.posted": "تم الترحيل",
    "businessUpload.linesRemain": "تبقى {{count}} بنود",
    "businessUpload.confidence": "ثقة",
    "businessUpload.language": "اللغة المقروءة",
    "businessUpload.language.arabic": "العربية",
    "businessUpload.language.english": "الإنجليزية",
    "businessUpload.language.bilingual": "العربية والإنجليزية",
    "businessUpload.language.unknown": "تعذر تأكيد اللغة",
    "businessUpload.receiptSettled": "تم اكتشاف إيصال مدفوع. سيُستورد كمُسوّى من المصدر ويُستبعد من المطابقة.",
    "businessUpload.auditedEvidence": "تم اكتشاف تقرير مراجع الحسابات.",
    "businessUpload.unauditedEvidence": "تم اكتشاف حسابات إدارية أو غير مدققة.",
    "businessUpload.rejectionHelp": "قسّم الملفات متعددة المستندات أو استخدم مستنداً مدعوماً. لم يستورد ميزانو أي بيانات.",
    "businessUpload.kind.bank_statement": "كشف حساب بنكي",
    "businessUpload.kind.bank_account_statement": "كشف حساب مصرفي",
    "businessUpload.kind.credit_card_statement": "كشف بطاقة ائتمان",
    "businessUpload.kind.invoice": "فاتورة",
    "businessUpload.kind.receipt": "إيصال مدفوع",
    "businessUpload.kind.contract": "عقد",
    "businessUpload.kind.financial_statement": "قوائم مالية",
    "businessUpload.kind.audited_financial_statement": "قوائم مالية مدققة",
    "businessUpload.kind.management_accounts": "حسابات إدارية",
    "businessUpload.kind.trial_balance": "ميزان مراجعة",
    "businessUpload.kind.entity_document": "مستند المنشأة",
    "businessUpload.kind.sms": "رسالة نصية",
    "businessUpload.kind.image": "صورة",
    "businessUpload.kind.unknown": "غير معروف / غير قابل للاستيراد",
    "businessUpload.kind.quotation": "عرض سعر",
    "businessUpload.kind.proforma_invoice": "فاتورة أولية",
    "businessUpload.kind.purchase_order": "أمر شراء",
    "businessUpload.kind.credit_note": "إشعار دائن",
    "businessUpload.kind.debit_note": "إشعار مدين",
    "businessUpload.kind.statement_of_account": "كشف حساب طرف مقابل",
    "businessUpload.kind.counterparty_directory": "دليل الأطراف المقابلة",
    "businessUpload.kind.delivery_note": "إذن تسليم",
    "businessUpload.kind.payroll": "مستند رواتب",
    "businessUpload.kind.multi_document": "مستندات متعددة",
    "businessUpload.rejection.quotation": "يبدو أن هذا عرض سعر. لا يوجد مبلغ مستحق حتى تصدر فاتورة، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.proforma_invoice": "يبدو أن هذه فاتورة أولية. ليست مطالبة واجبة الدفع، لذلك لا يستطيع ميزانو استيرادها بعد.",
    "businessUpload.rejection.purchase_order": "يبدو أن هذا أمر شراء. لا ينشئ التزاماً مستحقاً، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.credit_note": "يبدو أن هذا إشعار دائن. هو مستند تسوية وليس فاتورة سالبة، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.debit_note": "يبدو أن هذا إشعار مدين. هو مستند تسوية، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.statement_of_account": "يبدو أن هذا كشف حساب طرف مقابل وليس كشفاً بنكياً، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.counterparty_directory": "يبدو أن هذا دليل للأطراف المقابلة. لا يستطيع ميزانو دمج الهويات بأمان من دليل بعد، لذلك لم يستورده.",
    "businessUpload.rejection.delivery_note": "يبدو أن هذا إذن تسليم. لا يثبت وجود فاتورة، لذلك لا يستطيع ميزانو استيراده بعد.",
    "businessUpload.rejection.payroll": "يبدو أن هذا مستند رواتب. استيراد الرواتب غير مدعوم بعد.",
    "businessUpload.rejection.multi_document": "يحتوي هذا الملف على مستندات متعددة. قسّمه قبل الاستيراد حتى يمكن مراجعة كل مستند بأمان.",
    "businessUpload.rejection.unknown": "تعذر على ميزانو تحديد نوع مستند مدعوم. لم يتم استيراد أي بيانات.",
    "businessUpload.rejection.credit_card_statement": "يبدو أن هذا كشف بطاقة ائتمان. يحتوي على أرصدة البطاقة والمشتريات والمدفوعات والرسوم بدلاً من رصيد جاري لحساب بنكي، ولذلك لم يستورده ميزانو.",
    "businessAnalytics.title": "تحليلات الأعمال",
    "businessAnalytics.description": "حركة الحسابات شهرياً والمشتقة من ميزان المراجعة.",
    "businessAnalytics.monthByMonth": "شهراً بشهر",
    "businessAnalytics.account": "الحساب",
    "businessAnalytics.total": "الإجمالي",
    "businessAnalytics.expenseComposition": "توزيع المصروفات",
    "businessAnalytics.revenueByCounterparty": "الإيرادات حسب الطرف المقابل",
    "businessAnalytics.agingProfile": "تقادم الذمم المدينة والدائنة",
    "services.title": "الخدمات",
    "services.description": "أسعار الخدمات ومتوسط تكاليفها للاستخدام التشغيلي والإداري.",
    "services.operationalDisclosure": "تقديرات مبنية على متوسط التكاليف التي أدخلتها. لا تستخدم في قوائمك المالية.",
    "services.catalogue": "دليل الخدمات",
    "services.empty": "أضف خدمتك الأولى لعرض الهوامش التشغيلية.",
    "services.add": "إضافة خدمة",
    "services.edit": "تعديل",
    "services.save": "حفظ",
    "services.cancel": "إلغاء",
    "services.name": "اسم الخدمة",
    "services.nameAr": "اسم الخدمة بالعربية",
    "services.category": "الفئة",
    "services.noCategory": "بلا فئة",
    "services.unit": "الوحدة",
    "services.noUnit": "بلا وحدة",
    "services.price": "سعر البيع",
    "services.averageCost": "متوسط التكلفة",
    "services.margin": "الهامش",
    "services.marginPercent": "نسبة الهامش",
    "services.lossMaking": "متوسط التكلفة أعلى من سعر البيع. هذه الخدمة خاسرة في كل عملية بيع.",
    "services.recipe": "الوصفة / احتساب التكلفة",
    "services.recipeHint": "مكوّن واحد لكل سطر: الوصف | الكمية | تكلفة الوحدة. يظل ذلك تقديراً تشغيلياً.",
    "services.saveRecipe": "حفظ احتساب التكلفة",
    "services.suggestions": "اقتراحات قابلة للتعديل",
    "services.suggestionsHint": "الاقتراحات بلا أسعار أو تكاليف. أدخل أرقامك الخاصة قبل الحفظ.",
    "services.businessType.restaurant": "مطعم",
    "services.businessType.salon": "صالون",
    "services.businessType.repair": "إصلاح",
    "services.businessType.consulting": "استشارات",
    "services.marginsTitle": "هوامش الخدمات",
    "services.marginUnavailable": "تعذر تحميل هوامش الخدمات.",
    "services.unitsSold": "الوحدات المباعة",
    "services.revenue": "الإيراد",
    "services.estimatedCost": "التكلفة التقديرية",
    "services.noLinkedSales": "لا توجد مبيعات خدمات مرتبطة في هذه الفترة.",
    "services.totalEstimatedCost": "إجمالي التكلفة التقديرية للخدمات المقدمة",
    "services.actualCostOfSales": "التكلفة الفعلية للمبيعات من جرد المخزون",
    "services.unexplainedDifference": "الفرق غير المفسر",
    "services.reconciliationHint": "قد يمثل ذلك هدرًا أو سرقة أو استهلاكًا غير مسجل أو تسعيرًا غير مناسب للخدمة. إنه تحليل تشغيلي وليس محاسبة.",
    "services.invoiceLineLinks": "روابط الخدمات في بنود فواتير المبيعات",
    "services.invoiceLineHint": "هذه روابط تحليلية فقط، ولا تنشئ أي قيد يومية.",
    "services.addFromInvoiceLine": "إضافة كخدمة جديدة",
    "counterparties.openInvoices": "الفواتير المفتوحة",
    "invoice.amendTitle": "تصحيح وإعادة التحقق",
    "invoice.amendResubmit": "إعادة التحقق والإرسال",
    "invoice.invoiceNumberLabel": "رقم الفاتورة",
    "invoice.directionLabel": "الاتجاه",
    "invoice.counterpartyLabel": "الطرف المقابل",
    "invoice.currencyLabel": "العملة",
    "invoice.subtotalLabel": "المجموع الفرعي",
    "invoice.taxLabel": "الضريبة",
    "invoice.totalLabel": "الإجمالي",
    "businessStatements.title": "القوائم المالية للأعمال",
    "businessStatements.description": "قوائم مالية مشتقة من دفتر الأستاذ لكيان عملك.",
    "businessStatements.start": "بداية الفترة",
    "businessStatements.end": "نهاية الفترة",
    "businessStatements.unavailable": "القوائم المالية للأعمال غير جاهزة",
    "businessStatements.reportUnavailable": "تعذر تحميل القوائم المالية. أعد المحاولة أو ارجع إلى دفتر الأستاذ لمراجعة إعداد الأرصدة الافتتاحية.",
    "businessStatements.openingActionTitle": "لم يتم إعداد الأرصدة الافتتاحية",
    "businessStatements.openingMissing": "سجل قيدًا افتتاحيًا متوازنًا قبل إعداد القوائم المالية.",
    "businessStatements.openingReversed": "تم عكس القيد الافتتاحي السابق ولم يُستبدل. أنشئ قيدًا افتتاحيًا جديدًا وأكّده.",
    "businessStatements.noLedgerActivity": "لا توجد حركة في دفتر الأستاذ بعد. ابدأ بتسجيل الأرصدة الافتتاحية وتأكيدها.",
    "businessStatements.openingWizardLink": "فتح معالج الأرصدة الافتتاحية",
    "businessStatements.openingRequired": "سجل أرصدة افتتاحية متوازنة في دفتر الأستاذ قبل إعداد القوائم المالية.",
    "businessStatements.crNumber": "رقم السجل التجاري",
    "businessStatements.noCr": "رقم السجل التجاري غير مسجل",
    "businessStatements.exportPdf": "تصدير PDF",
    "businessStatements.exportExcel": "تصدير Excel",
    "businessStatements.exportBlocked": "التصدير محظور حتى تتوازن الميزانية العمومية.",
    "businessStatements.allowPopup": "اسمح بالنوافذ المنبثقة لتصدير ملف PDF.",
    "businessStatements.pdfFailed": "تعذر إنشاء ملف PDF. حاول مرة أخرى.",
    "businessStatements.footer": "أُعد من مستندات العميل. غير مدقق. / Prepared from client-provided documents. Not audited.",
    "inventoryCount.title": "جرد المخزون",
    "inventory.title": "المخزون والهوامش",
    "inventory.description": "المخزون وتكلفة المبيعات والهامش الإجمالي مستمدة من دفتر الأستاذ. الكميات وتكلفة كل صنف غير متاحة لأن ميزانو لا يحتفظ بدفتر أصناف.",
    "inventory.currentBalance": "رصيد المخزون الحالي",
    "inventory.grossMargin": "الهامش الإجمالي",
    "inventory.noItemSubledger": "لا يحتفظ ميزانو بدفتر أصناف، ولذلك لا تتوفر الكميات أو رموز الأصناف أو تكلفة كل صنف.",
    "inventory.purchaseLines": "بنود المشتريات",
    "inventory.classifiedLines": "بنود المخزون المصنفة",
    "inventory.unclassifiedLines": "بنود تاريخية غير معروفة التصنيف",
    "inventory.historicalUnknown": "صُنفت قبل تسجيل تفصيل البنود.",
    "inventory.supplier": "المورد",
    "inventory.sourceInvoice": "فاتورة المصدر",
    "inventory.lineDescription": "الوصف",
    "inventory.lineAmount": "قيمة البند",
    "inventory.classifiedTotal": "إجمالي بنود المخزون المصنفة",
    "inventory.ledgerDifference": "الفرق عن رصيد الحساب 1300",
    "inventoryCount.description": "أكّد قيمة المخزون في نهاية الفترة. لا يقدّر ميزانو استهلاك المخزون من المبيعات.",
    "inventoryCount.add": "تسجيل جرد المخزون",
    "inventoryCount.date": "تاريخ الجرد",
    "inventoryCount.closing": "قيمة المخزون الختامي",
    "inventoryCount.method": "طريقة الجرد",
    "inventoryCount.physical": "جرد فعلي",
    "inventoryCount.estimate": "تقدير الإدارة",
    "inventoryCount.notes": "ملاحظات (اختياري)",
    "inventoryCount.document": "مستند ورقة الجرد الداعم (اختياري)",
    "inventoryCount.preview": "احتساب تكلفة المبيعات",
    "inventoryCount.opening": "مخزون أول المدة",
    "inventoryCount.purchases": "مشتريات المخزون",
    "inventoryCount.cogs": "تكلفة المبيعات",
    "inventoryCount.confirm": "تأكيد وترحيل التسوية",
    "inventoryCount.zeroConfirm": "أؤكد أن المخزون الختامي صفر رغم وجود مشتريات جوهرية.",
    "inventoryCount.history": "جرد المخزون المؤكد",
    "inventoryCount.confirmedBy": "أكده",
    "inventoryCount.reversed": "تم العكس",
    "inventoryCount.estimateDisclosure": "تكلفة المبيعات لهذه الفترة مبنية على تقدير الإدارة وليست على جرد فعلي.",
    "inventoryCount.physicalDisclosure": "تكلفة المبيعات لهذه الفترة مبنية على جرد فعلي مؤكد.",
    "inventoryCount.health": "يوجد مخزون وإيرادات مسجلة، لكن لم تُسجل تكلفة مبيعات لهذه الفترة.",
    "inventoryCount.healthFigures": "المخزون: {inventory} د.ك · الإيرادات: {revenue} د.ك",
    "inventoryCount.chartMissingTitle": "دليل الحسابات غير مُعد",
    "inventoryCount.chartMissing": "لا تتوفر جردات المخزون حتى يُعد دليل حسابات لهذه المنشأة. أعد إعداد أو إصلاح دليل حسابات الأعمال ثم حاول مرة أخرى.",
    "inventoryCount.fileReadFailed": "تعذرت قراءة المستند الداعم.",
    "inventoryCount.uploadFailed": "تعذر رفع المستند الداعم.",
    "invoice.healthInventoryWithoutCogs": "يوجد مخزون دون تسجيل تكلفة المبيعات",
    "invoice.healthMissingChart": "لم يتم إعداد دليل الحسابات",
    "businessStatements.excluded": "الفواتير المستبعدة للمراجعة",
    "businessStatements.excludedDescription": "هذه الفواتير تحتاج مراجعة ولا يتم تضمينها في القوائم المشتقة من دفتر الأستاذ.",
    "businessStatements.balanceMismatch": "الميزانية العمومية غير متوازنة",
    "businessStatements.income": "قائمة الدخل",
    "businessStatements.balance": "الميزانية العمومية",
    "businessStatements.cash": "التدفقات النقدية",
    "businessStatements.comparative": "الفترة الحالية مع مقارنة الفترة السابقة",
    "businessStatements.showComparative": "إظهار مقارنة الفترة السابقة",
    "businessStatements.currentOnly": "الفترة الحالية فقط",
    "businessStatements.account": "الحساب",
    "businessStatements.current": "الحالي",
    "businessStatements.prior": "السابق",
    "businessStatements.subtotal": "المجموع الفرعي",
    "businessStatements.profit": "ربح الفترة",
    "businessStatements.assets": "الأصول",
    "businessStatements.liabilities": "الالتزامات",
    "businessStatements.equity": "حقوق الملكية",
    "businessStatements.changesInEquity": "قائمة التغيرات في حقوق الملكية",
    "businessStatements.trialBalance": "ميزان المراجعة",
    "businessStatements.generalLedger": "دفتر الأستاذ العام",
    "xbrl.title": "تصدير XBRL",
    "xbrl.draft": "مسودة فقط — غير مخصصة للتقديم للبوابة ما دامت مفاهيم التصنيف غير موثقة.",
    "xbrl.taxonomy": "إصدار التصنيف",
    "xbrl.unverified": "المفاهيم غير الموثقة",
    "xbrl.unmapped": "الحسابات غير المعيّنة",
    "xbrl.generateInline": "إنشاء iXBRL (.xhtml)",
    "xbrl.instanceDownloadTitle": "مثيل XBRL (.xml)",
    "xbrl.inlineDownloadTitle": "XBRL مضمّن (.xhtml)",
    "xbrl.reviewDownloadsDescription": "ملفات مراجعة قابلة للقراءة آلياً وبشرياً. تستخدم نفس عناصر قيد الرسمية وقواعد التجميع المستخدمة في المصنف، لكنها تبقى مسودات فقط.",
    "xbrl.generateInstance": "إنشاء XBRL (.xml)",
    "xbrl.mapping": "مراجعة التعيين",
    "xbrl.concept": "مفهوم التصنيف",
    "xbrl.verified": "موثق",
    "xbrl.notMapped": "غير معيّن",
    "xbrl.history": "سجل الإنشاء",
    "xbrl.facts": "حقائق",
    "xbrl.blocked": "إنشاء XBRL محظور حتى تجتاز فحوصات القوائم المالية.",
    "xbrl.crRequired": "إنشاء XBRL محظور لأن هذا الكيان لا يحمل رقم السجل التجاري. لا يمكن للملف تحديد هوية الكيان بدونه.",
    "xbrl.editEntity": "أضف رقم السجل التجاري من إدارة الكيانات",
    "businessStatements.opening": "الرصيد الافتتاحي",
    "businessStatements.periodDebits": "مدين الفترة",
    "businessStatements.periodCredits": "دائن الفترة",
    "businessStatements.closing": "الرصيد الختامي",
    "businessStatements.hideZero": "إخفاء الحسابات بلا حركة",
    "businessStatements.selectAccount": "اختر حسابًا",
    "businessStatements.runningBalance": "الرصيد الجاري",
    "businessStatements.source": "المصدر",
    "businessStatements.noLedgerLines": "لا توجد قيود يومية في هذه الفترة.",
    "businessStatements.generalLedgerDescription": "اختر حساباً لتتبع كل سطر قيد ومرجعه ورصيده الجاري.",
    "businessStatements.date": "التاريخ",
    "businessStatements.memo": "البيان",
    "businessStatements.equityDescription": "حركة رأس المال والأرباح المحتجزة والمسحوبات مشتقة من دفتر الأستاذ، مع ثلاث مطابقات واضحة.",
    "businessStatements.equityMovement": "الحركة",
    "businessStatements.equityCapital": "رأس المال",
    "businessStatements.equityRetained": "الأرباح المحتجزة",
    "businessStatements.equityDrawings": "المسحوبات",
    "businessStatements.equityTotal": "إجمالي حقوق الملكية",
    "businessStatements.equityRow.opening": "رصيد البداية",
    "businessStatements.equityRow.profit": "ربح الفترة",
    "businessStatements.equityRow.contributions": "مساهمات الملاك",
    "businessStatements.equityRow.drawings": "مسحوبات الملاك",
    "businessStatements.equityRow.adjustments": "تعديلات حقوق الملكية",
    "businessStatements.equityRow.closing": "رصيد الختام",
    "businessStatements.profitTieOut": "الربح يطابق قائمة الدخل",
    "businessStatements.equityTieOut": "حقوق الملكية الختامية تطابق الميزانية العمومية",
    "businessStatements.openingTieOut": "حقوق الملكية الافتتاحية تطابق الميزانية السابقة",
    "businessStatements.liabilitiesEquity": "الالتزامات + حقوق الملكية",
    "businessStatements.balanceCheck": "الأصول = الالتزامات + حقوق الملكية",
    "businessStatements.openingDerivedDisclosure": "الأرباح المحتجزة الافتتاحية بمبلغ {amount} د.ك مشتقة من مدخلات افتتاحية مؤكدة ولم يتم التحقق منها بعد.",
    "businessStatements.balanced": "متوازن",
    "businessStatements.cashDescription": "طريقة غير مباشرة من دفتر الأستاذ، مع مطابقة لأرصدة البنوك المرفوعة.",
    "businessStatements.depreciation": "الإهلاك",
    "businessStatements.receivablesMovement": "حركة الذمم المدينة",
    "businessStatements.payablesAccruals": "حركة الذمم الدائنة والمستحقات",
    "businessStatements.operating": "صافي التدفقات النقدية التشغيلية",
    "businessStatements.investing": "صافي التدفقات النقدية الاستثمارية",
    "businessStatements.financing": "صافي التدفقات النقدية التمويلية",
    "businessStatements.closingCash": "النقد الختامي",
    "businessStatements.cashReconciliation": "مطابقة النقد",
    "businessStatements.cashNotComparable": "غير مطابق — لا يوجد حساب بنكي مرتبط بهذه المنشأة.",
    "businessStatements.capitalAssumedMinimum": "رأس المال معروض عند الحد الأدنى القانوني البالغ 1,000 د.ك. حدّثه من مستندات التأسيس أو أدخل رأس المال المدفوع الفعلي.",
    "businessStatements.importedStatementDisclosure": "أُعدت من أرقام مستخرجة من قوائم مالية مرفوعة، وليس من سجلات المعاملات الأساسية.",
    "ledger.statementImportTitle": "استيراد قوائم مالية كاملة",
    "ledger.statementImportDescription": "استخرج الميزانية وقائمة الربح أو الخسارة بالكامل، واربط كل بند وراجعه ثم رحّل قيدين قابلين للتتبع.",
    "ledger.statementExtract": "استخراج القائمة الكاملة",
    "ledger.statementSelectDocument": "اختر مستند قوائم مالية مرفوعاً أولاً.",
    "ledger.statementLine": "بند المصدر",
    "ledger.statementStatus": "حالة المراجعة",
    "ledger.statementExclude": "استبعاد صريح",
    "ledger.statementMapped": "مرتبط",
    "ledger.statementExcluded": "مستبعد",
    "ledger.statementUnmapped": "يتطلب ربطاً",
    "ledger.statementEquityMovements": "حركات حقوق ملكية صريحة",
    "ledger.statementEquityHelp": "أضف توزيعات الأرباح أو الدخل الشامل الآخر أو تحركات رأس المال أو أي تغيرات موثقة في حقوق الملكية لازمة للمطابقة. لا تنشئ ميزانو رصيداً مكملاً للأرباح المحتجزة.",
    "ledger.statementPreview": "معاينة المطابقة",
    "ledger.statementPost": "ترحيل الاستيراد المراجع ذي القيدين",
    "ledger.statementActivityDifference": "فرق حركة الفترة",
    "ledger.statementBalanceDifference": "فرق ميزانية المصدر",
    "ledger.statementDiagnosis": "التشخيص",
    "businessStatements.statementCash": "النقد الختامي حسب القائمة",
    "businessStatements.bankCash": "أرصدة البنوك الختامية",
    "businessStatements.difference": "الفرق",
    "businessStatements.subtype.current_asset": "الأصول المتداولة",
    "businessStatements.subtype.noncurrent_asset": "الأصول غير المتداولة",
    "businessStatements.subtype.contra_asset": "الأصول المقابلة",
    "businessStatements.subtype.current_liability": "الالتزامات المتداولة",
    "businessStatements.subtype.noncurrent_liability": "الالتزامات غير المتداولة",
    "businessStatements.subtype.equity": "حقوق الملكية",
    "businessStatements.subtype.operating_revenue": "الإيرادات التشغيلية",
    "businessStatements.subtype.other_revenue": "إيرادات أخرى",
    "businessStatements.subtype.cost_of_sales": "تكلفة المبيعات",
    "businessStatements.subtype.operating_expense": "المصروفات التشغيلية",
    "businessStatements.subtype.other_expense": "مصروفات أخرى",
    "businessStatements.subtype.other": "أخرى",
    "landing.businessInDevelopment": "قيد التطوير",
    "entity.switcher": "الكيان",
    "entity.manage": "إدارة الكيانات",
    "entity.title": "إدارة الكيانات",
    "entity.description": "أنشئ كيانات واختر الكيان الذي تعرض بياناته المالية.",
    "entity.create": "إنشاء كيان",
    "entity.name": "اسم الكيان",
    "entity.nameAr": "الاسم بالعربية (اختياري)",
    "entity.type": "نوع الكيان",
    "entity.company": "شركة",
    "entity.soleProprietor": "مؤسسة فردية",
    "entity.individual": "فردي",
    "entity.businessType": "نوع النشاط",
    "entity.businessTypeRetail": "تجارة التجزئة",
    "entity.businessTypeServices": "خدمات",
    "entity.businessTypeBoth": "تجارة التجزئة والخدمات",
    "entity.crNumber": "رقم السجل التجاري (اختياري)",
    "entity.organisationCivilId": "الرقم المدني للجهة (قيد)",
    "entity.organisationCivilIdHint": "تستخدم قيد الرقم المدني للجهة. وهو مختلف عن رقم السجل التجاري ولا يُنسخ منه أبداً.",
    "entity.invalidOrganisationCivilId": "أدخل الرقم المدني للجهة باستخدام الأرقام فقط.",
    "entity.qaydMetadata": "بيانات إيداع قيد",
    "entity.qaydMetadataDescription": "حقائق الشركة المطلوبة لتصدير مصنف قيد. بيانات توثيق مراقب الحسابات اختيارية ولا تُدخل إلا إذا أكدها تقرير مراقب الحسابات.",
    "entity.qaydAuditorReportOnly": "إذا أُدخلت بيانات رقم التوثيق وتاريخه ورقم قيد مراقب الحسابات واسمه واسم المكتب، فيجب أن تأتي من تقرير مراقب الحسابات. لا ينشئ ميزانو هذه البيانات ولا يستنتجها.",
    "entity.qaydConfirmedDefaults": "تُعبأ الكويت والدينار الكويتي والشكل القانوني المقترح من بيانات الكيان لتأكيدك عند الحفظ.",
    "entity.auditNo": "رقم التوثيق",
    "entity.auditDate": "تاريخ التوثيق",
    "entity.auditorRegistrationNumber": "رقم قيد مراقب الحسابات",
    "entity.auditorName": "اسم مراقب الحسابات",
    "entity.accountingOffice": "اسم المكتب",
    "entity.legalFormOfEntity": "الشكل القانوني للمنشأة",
    "entity.registeredOfficeAddress": "عنوان المكتب المسجل",
    "entity.natureOfOperations": "طبيعة العمليات",
    "entity.countryOfIncorporation": "بلد التأسيس",
    "entity.presentationCurrencyDescription": "وصف عملة العرض",
    "entity.qaydRounding": "تقريب قيد",
    "entity.qaydActualValue": "القيمة الفعلية (دون تقريب)",
    "entity.contactEmail": "البريد الإلكتروني للتواصل مع الأعمال",
    "entity.makeDefault": "تعيين ككيان نشط",
    "entity.active": "الكيان النشط",
    "entity.created": "تم إنشاء الكيان",
    "entity.updated": "تم تحديث الكيان",
    "entity.detailsDescription": "حدّث التفاصيل المستخدمة في مساحة عمل الأعمال النشطة.",
    "entity.save": "حفظ تفاصيل الأعمال",
    "entity.saving": "جارٍ الحفظ…",
    "entity.fiscalYearEnd": "نهاية السنة المالية",
    "entity.fiscalYearEndMonth": "الشهر",
    "entity.fiscalYearEndDay": "اليوم",
    "entity.xbrlEntryPoint": "نقطة إدخال XBRL",
    "entity.xbrlSme": "المعايير الدولية للمنشآت الصغيرة والمتوسطة",
    "entity.xbrlFull": "المعايير الدولية الكاملة",
    "entity.crFilingWarning": "تغيير رقم السجل التجاري يغيّر المعرّف المستخدم في إيداعات XBRL المستقبلية. تحتفظ الإيداعات التي تم إنشاؤها سابقاً بمعرّفها الحالي ولا تتغير.",
    "entity.fiscalYearWarning": "لدى هذه المنشأة قيود يومية بالفعل. تغيير نهاية سنتها المالية يعيد تحديد الفترة لكل قائمة مالية.",
    "entity.currentFiscalYearEnd": "نهاية الفترة الحالية",
    "entity.newFiscalYearEnd": "نهاية الفترة الجديدة",
    "entity.confirmFiscalYearEnd": "أفهم وأرغب في إعادة تحديد فترات القوائم",
    "entity.fiscalConfirmationRequired": "أكّد تغيير السنة المالية قبل الحفظ.",
    "entity.invalidFiscalYearEnd": "اختر تاريخاً فعلياً لنهاية السنة المالية.",
    "entity.invalidCrNumber": "أدخل رقم سجل تجاري مكوّناً من أرقام فقط.",
    "entity.businessRequired": "بدّل إلى حساب أعمال لإنشاء كيانات إضافية.",
    "entity.documents": "مستندات",
    "entity.transactions": "معاملات",
    "entity.journalEntries": "قيود يومية",
    "entity.contacts": "جهات اتصال",
    "entity.delete": "حذف الكيان",
    "entity.deleted": "تم حذف الكيان",
    "entity.deleteConfirm": "اكتب DELETE لإزالة هذا الكيان غير المستخدم نهائياً.",
    "entity.adminCascadeDelete": "حذف إداري شامل لجميع السجلات",
    "entity.adminCascadeWarning": "سيؤدي هذا إلى حذف المنشأة وجميع سجلاتها نهائياً. لا يمكن التراجع عن هذا الإجراء.",
    "entity.adminCascadeConfirm": "اكتب {{name}} تماماً لتأكيد الحذف الإداري.",
    "entity.adminCascadeCounts": "سيتم حذف: {{documents}} مستنداً و{{journals}} قيداً و{{invoices}} فاتورة و{{contacts}} جهة اتصال و{{contracts}} عقداً و{{filings}} ملف XBRL.",
    "entity.adminCascadeDeleted": "تم حذف المنشأة والسجلات التابعة لها.",
    "entity.duplicateCrWarning": "رقم السجل التجاري هذا موجود بالفعل لدى أحد كياناتك. أنشئ كياناً آخر بنفس الرقم فقط إذا كان ذلك مقصوداً.",
    "entity.confirmDuplicateCr": "إنشاء بهذا الرقم التجاري",
    "businessUpload.supported": "PDF أو JPG أو PNG أو WEBP حتى 10 ميغابايت",
    "businessUpload.choose": "اختر ملفاً",
    "businessAnalytics.revenue": "الإيرادات",
    "businessAnalytics.expenses": "المصروفات",
    "businessAnalytics.profit": "الربح",
    "invoice.title": "الفواتير",
    "invoice.description": "ارفع وراجع فواتير العملاء والموردين للكيان النشط.",
    "invoice.upload": "رفع فاتورة",
    "invoice.uploadHint": "PDF أو JPG أو PNG أو WEBP حتى 10 ميغابايت",
    "invoice.reviewQueue": "قائمة المراجعة",
    "invoice.aging": "أعمار الذمم المدينة والدائنة",
    "invoice.all": "كل الفواتير",
    "invoice.receivable": "مدين",
    "invoice.payable": "دائن",
    "invoice.direction": "الاتجاه",
    "invoice.status": "الحالة",
    "invoice.counterparty": "الطرف المقابل",
    "invoice.dateRange": "نطاق التاريخ",
    "invoice.needsReview": "تحتاج مراجعة",
    "invoice.reviewReason.general": "راجع بيانات الفاتورة المستخرجة",
    "invoice.reviewReason.counterparty_unrecognised": "لم يتم التعرّف على الطرف المقابل",
    "invoice.reviewReason.direction_unresolved": "يلزم تأكيد اتجاه الفاتورة",
    "invoice.reviewReason.invoice_number_unclear": "رقم الفاتورة غير واضح",
    "invoice.reviewReason.currency_unconfirmed": "العملة غير مؤكدة",
    "invoice.reviewReason.arithmetic_check_failed": "تحتاج حسابات الفاتورة إلى مراجعة",
    "invoice.reviewReason.line_caption_unclassified": "تعذر تصنيف بند شراء: راجع الوصف المذكور",
    "invoice.noInvoices": "لا توجد فواتير",
    "invoice.open": "مفتوحة",
    "invoice.paid": "مدفوعة",
    "invoice.partiallyPaid": "مدفوعة جزئياً",
    "invoice.draft": "مسودة",
    "invoice.void": "ملغاة",
    "invoice.current": "0–30 يوماً",
    "invoice.days31to60": "31–60 يوماً",
    "invoice.days61to90": "61–90 يوماً",
    "invoice.days90plus": "90+ يوماً",
    "invoice.extractedValues": "القيم المستخرجة",
    "invoice.currentValue": "القيمة الحالية",
    "invoice.sourceEvidence": "دليل المصدر",
    "invoice.settledAtSource": "مسددة في المستند المصدر",
    "invoice.settledAtSourceDescription": "لا يُتوقع تطابق بنكي لأن هذه الفاتورة تحمل علامة سداد صريحة في المستند المصدر.",
    "invoice.invoiceAmount": "مبلغ الفاتورة",
    "invoice.kwdEquivalent": "المعادل بالدينار الكويتي",
    "invoice.fxRate": "سعر الصرف المستخدم",
    "invoice.currencyUnconfirmed": "العملة تحتاج إلى مراجعة",
    "invoice.businessRequired": "تتوفر الفواتير عند تفعيل وضع الأعمال.",
    "invoice.health": "صحة الكيان",
    "invoice.healthDescription": "فحوصات سلامة الكيان النشط. حدّث لإعادة التشغيل.",
    "invoice.refreshHealth": "تحديث الصحة",
    "invoice.healthClear": "لا توجد مشكلات",
    "invoice.healthUnconfirmedCounterparties": "أطراف مقابلة تحتاج إلى تأكيد",
    "invoice.healthNeedsReview": "ما زالت تحتاج مراجعة",
    "invoice.healthMissingCounterparty": "طرف مقابل مفقود",
    "invoice.healthPastDueNoPayment": "متأخرة بلا دفعة مسجلة",
    "invoice.healthOverSettled": "مدفوعة بأكثر من إجمالي الفاتورة",
    "invoice.healthBankChains": "فشل سلسلة أرصدة البنك",
    "invoice.healthUnmatchedPayments": "دفعات بلا مطابقة فاتورة",
    "invoice.healthUnbalancedJournals": "قيود دفتر غير متوازنة",
    "invoice.healthPeriodContinuity": "الافتتاحي + الحركة ≠ الختامي",
    "invoice.healthCashFlowReconciliation": "التدفق النقدي غير مطابق لرصيد بنكي",
    "invoice.healthStuckDocuments": "مستندات عالقة في المعالجة",
    "invoice.cashFlowNotComparable": "غير قابل للمقارنة — لا يوجد حساب بنكي مرتبط بهذه المنشأة",
    "invoice.healthReviewExcluded": "فواتير مستبعدة من القوائم",
    "contract.title": "العقود",
    "contract.description": "استخرج شروط العقد للمراجعة ثم أنشئ جداول الإيرادات أو الدفعات المتوقعة.",
    "contract.businessRequired": "تتوفر العقود عند تفعيل وضع الأعمال.",
    "contract.upload": "رفع عقد",
    "contract.alerts": "تنبيهات العقود",
    "contract.overdue": "دفعات متوقعة لم تصل",
    "contract.expiring": "قرب الانتهاء أو التجديد",
    "contract.none": "لا توجد عقود",
    "contract.reviewTitle": "مراجعة شروط العقد",
    "contract.reviewWarning": "لا تُسجَّل العقود في دفتر الأستاذ تلقائياً. أكّد كل شرط قبل أن ينشئ ميزانو جدولاً.",
    "contract.confirmedTerms": "الشروط المطلوب تأكيدها",
    "contract.reviewRequired": "تحتاج مراجعة",
    "contract.counterparty": "الطرف المقابل",
    "contract.type": "نوع العقد",
    "contract.startDate": "تاريخ البدء",
    "contract.endDate": "تاريخ الانتهاء",
    "contract.total": "القيمة الإجمالية",
    "contract.frequency": "التكرار",
    "contract.amountPerPeriod": "المبلغ لكل فترة",
    "contract.autoRenew": "تجديد تلقائي",
    "contract.yes": "نعم",
    "contract.no": "لا",
    "contract.confirmAll": "تأكيد كل الشروط وإنشاء الجدول",
    "contract.sourceEvidence": "دليل المصدر",
    "contract.noEvidence": "لم يُستخرج نص داعم",
    "contract.schedule": "الجدول المتوقع",
    "contract.linkInvoice": "ربط فاتورة",
    "contract.linkPayment": "ربط دفعة",
    "contract.type.lease": "إيجار",
    "contract.type.service": "خدمة",
    "contract.type.employment": "توظيف",
    "contract.type.revenue": "إيراد",
    "contract.type.other": "أخرى",
    "contract.status.draft": "مسودة",
    "contract.status.active": "نشط",
    "contract.status.expired": "منتهي",
    "contract.status.terminated": "ملغى",
    "contract.frequency.one_time": "مرة واحدة",
    "contract.frequency.monthly": "شهري",
    "contract.frequency.quarterly": "ربع سنوي",
    "contract.frequency.annual": "سنوي",
    "contract.scheduleStatus.expected": "متوقع",
    "contract.scheduleStatus.invoiced": "تمت فوترته",
    "contract.scheduleStatus.settled": "تمت تسويته",
    "contract.scheduleStatus.overdue": "متأخر",
    "contract.evidence.counterpartyName": "دليل الطرف المقابل",
    "contract.evidence.contractType": "دليل النوع",
    "contract.evidence.startDate": "دليل تاريخ البدء",
    "contract.evidence.endDate": "دليل تاريخ الانتهاء",
    "contract.evidence.totalValue": "دليل القيمة الإجمالية",
    "contract.evidence.currency": "دليل العملة",
    "contract.evidence.frequency": "دليل التكرار",
    "contract.evidence.amountPerPeriod": "دليل المبلغ لكل فترة",
    "contract.evidence.autoRenew": "دليل التجديد",
    "contract.evidence.renewalTerms": "دليل شروط التجديد",
    "reconciliation.title": "المطابقة",
    "reconciliation.description": "راجع الروابط المقترحة بين الفواتير والمعاملات البنكية. لا يُسجّل أي شيء قبل تأكيدك.",
    "reconciliation.businessRequired": "تتوفر المطابقة عند تفعيل وضع الأعمال.",
    "reconciliation.refresh": "تحديث الاقتراحات",
    "reconciliation.confirmationOnly": "لا تغيّر الاقتراحات دفاترك تلقائياً. أكّد كل تخصيص، ويمكنك إلغاء المطابقة في أي وقت لاستعادة حالة الفاتورة.",
    "reconciliation.suggestions": "المطابقات المقترحة",
    "reconciliation.loading": "جارٍ فحص المعاملات البنكية المؤهلة…",
    "reconciliation.noSuggestions": "لم يتم العثور على مطابقات فواتير قابلة للمراجعة.",
    "reconciliation.invoice": "الفاتورة",
    "reconciliation.transaction": "المعاملة",
    "reconciliation.unknownTransaction": "معاملة غير معروفة",
    "reconciliation.amountDifference": "الفرق",
    "reconciliation.amountDifferenceConfirmation": "أؤكد أن مبلغ الدفعة يختلف عن الفاتورة بأكثر من 1.000 د.ك.",
    "reconciliation.remaining": "المتبقي من الفاتورة",
    "reconciliation.amountApplied": "المبلغ المخصص",
    "reconciliation.reject": "رفض",
    "reconciliation.confirm": "تأكيد المطابقة",
    "reconciliation.reviewConfirm": "راجع وأكّد",
    "reconciliation.highConfidence": "مقترحة",
    "reconciliation.reviewRequired": "تتطلب مراجعة",
    "reconciliation.suggestedOnly": "لا تسجيل تلقائي",
    "reconciliation.confirmedMatches": "المطابقات المؤكدة",
    "reconciliation.noConfirmedMatches": "لا توجد مطابقات مؤكدة بعد.",
    "reconciliation.unmatch": "إلغاء المطابقة",
    "documents.title": "المستندات",
    "documents.description": "مستنداتك المالية المرفوعة للكيان النشط.",
    "documents.allTypes": "كل الأنواع",
    "documents.bankStatement": "كشف حساب بنكي",
    "documents.financialStatement": "قائمة مالية",
    "documents.entityDocument": "مستند الكيان",
    "documents.invoice": "فاتورة",
    "documents.contract": "عقد",
    "documents.status": "الحالة",
    "documents.uploaded": "تم الرفع",
    "documents.processing": "قيد المعالجة",
    "documents.parsed": "تمت القراءة",
    "documents.failed": "فشل",
    "documents.reviewed": "تمت المراجعة",
    "documents.empty": "لا توجد مستندات لهذا الكيان حتى الآن.",
    "documents.retry": "إعادة رفع المستند",
    "documents.retryReady": "المستند جاهز لإعادة المحاولة. ارفعه مجددًا لبدء تحليل جديد.",
    "documents.delete": "حذف",
    "documents.deleted": "تم حذف المستند",
    "documents.statementImport": "استيراد القوائم المالية",
    "documents.statementPeriod": "الفترة",
    "documents.statementPurpose": "الغرض",
    "documents.openingBalances": "الأرصدة الافتتاحية",
    "documents.fullPeriod": "الفترة الكاملة",
    "documents.statementStatus": "الحالة",
    "documents.mapped": "تمت المطابقة",
    "documents.unmapped": "غير مطابق",
    "documents.viewPostedImport": "عرض الاستيراد المرحّل",
    "documents.resumeReview": "استئناف المراجعة (تبقى {{count}} بنود)",
    "documents.noFinancialStatements": "لا توجد قوائم مالية بعد",
    "documents.noFinancialStatementsDescription": "ارفع قائمة مالية لبدء مراجعة مطابقة قابلة للاستئناف.",
    "counterparties.title": "الأطراف المقابلة",
    "counterparties.description": "إدارة العملاء والموردين للكيان النشط.",
    "counterparties.search": "البحث في الأطراف المقابلة",
    "counterparties.create": "إنشاء طرف مقابل",
    "counterparties.edit": "تعديل",
    "counterparties.merge": "دمج المكررات",
    "counterparties.name": "الاسم",
    "counterparties.nameAr": "الاسم بالعربية",
    "counterparties.type": "النوع",
    "counterparties.customer": "عميل",
    "counterparties.vendor": "مورد",
    "counterparties.both": "عميل ومورد",
    "counterparties.taxId": "الرقم الضريبي",
    "counterparties.email": "بريد التواصل",
    "counterparties.phone": "هاتف التواصل",
    "counterparties.notes": "ملاحظات",
    "counterparties.save": "حفظ الطرف المقابل",
    "counterparties.cancel": "إلغاء",
    "counterparties.aliases": "الأسماء البديلة",
    "counterparties.unconfirmed": "يحتاج إلى تأكيد",
    "counterparties.invoiceCount": "الفواتير",
    "counterparties.openBalance": "الرصيد المفتوح",
    "counterparties.purchased": "المشتريات",
    "counterparties.sold": "المبيعات",
    "counterparties.ledgerDefault": "حساب الأستاذ الافتراضي للفواتير القادمة",
    "counterparties.selectLedgerDefault": "اختر حساب إيراد أو مصروف",
    "counterparties.ledgerDefaultRequired": "اختر حساب الأستاذ الافتراضي قبل تأكيد الطرف المقابل.",
    "counterparties.confirmWithLedgerDefault": "تأكيد وحفظ الحساب الافتراضي",
    "counterparties.confirmedWithLedgerDefault": "تم تأكيد الطرف المقابل وحفظ الحساب الافتراضي.",
    "counterparties.confirm": "تأكيد",
    "counterparties.bankNarration": "وصف البنك المؤكد",
    "counterparties.confirmNarration": "تأكيد الوصف",
    "counterparties.empty": "لا توجد أطراف مقابلة لهذا الكيان بعد.",
    "counterparties.created": "تم إنشاء الطرف المقابل",
    "counterparties.updated": "تم تحديث الطرف المقابل",
    "counterparties.merged": "تم دمج الأطراف المقابلة",
    "counterparties.aliasSaved": "تم حفظ وصف البنك كاسم بديل",
    "counterparties.selectSource": "المكرر المطلوب دمجه",
    "counterparties.selectTarget": "الطرف المقابل المحتفظ به",

    // Navigation
    "nav.dashboard": "لوحة التحكم",
    "nav.smsParser": "رفع المعاملات",
    "nav.transactions": "المعاملات",
    "nav.incomeStatement": "كشف الدخل",
    "nav.analytics": "التحليلات",
    "nav.budgets": "الميزانيات",
    "nav.export": "تصدير",
    "nav.setup": "الإعدادات",
    "nav.more": "المزيد",
    "nav.accounts": "الحسابات",
    "nav.accountSettings": "إعدادات الحساب",
    "nav.upload": "رفع",
    "nav.contacts": "جهات الاتصال",

    // User menu
    "menu.lightMode": "الوضع الفاتح",
    "menu.darkMode": "الوضع الداكن",
    "menu.signOut": "تسجيل الخروج",
    "menu.exitDemo": "الخروج من العرض",
    "menu.language": "English",

    // Dashboard
    "dashboard.title": "لوحة التحكم",
    "dashboard.cashIn": "الداخل",
    "dashboard.cashOut": "الخارج",
    "dashboard.netCashFlow": "صافي التدفق النقدي",
    "dashboard.latestBalance": "آخر رصيد",
    "dashboard.topSpending": "أعلى فئات الإنفاق",
    "dashboard.recentTransactions": "آخر المعاملات",
    "dashboard.transactions": "معاملات",
    "dashboard.welcome": "مرحباً بك في ميزانو!",
    "dashboard.welcomeDesc": "ابدأ برفع كشف حسابك البنكي أو رسائل SMS.",
    "dashboard.goToSMS": "رفع المعاملات",
    "dashboard.emptyTitle": "لا توجد معاملات بعد",
    "dashboard.emptyDesc": "ارفع كشف حساب بنكي للبدء وعرض ملخصك المالي.",
    "dashboard.queryUnavailable": "تعذّر تحميل لوحة التحكم",
    "dashboard.queryTimeout": "يستغرق تحميل لوحة التحكم وقتاً أطول من المتوقع",
    "dashboard.queryRetryDescription": "تحقق من اتصالك ثم حاول مرة أخرى. لم يتم تغيير بياناتك المالية.",
    "dashboard.retry": "حاول مرة أخرى",
    "route.loading": "جارٍ التحميل",
    "route.timeoutTitle": "يستغرق تحميل هذه الصفحة وقتاً أطول من المتوقع",
    "route.errorTitle": "تعذّر تحميل هذه الصفحة",
    "route.errorOrigin": "مصدر الخطأ",
    "route.retryDescription": "تحقق من اتصالك ثم حاول مرة أخرى. لم يتم تغيير بياناتك.",
    "route.retry": "حاول مرة أخرى",
    "dashboard.noSpending": "لا توجد بيانات إنفاق بعد",
    "dashboard.topEarning": "أعلى فئات الدخل",
    "dashboard.noEarning": "لا توجد بيانات دخل بعد",
    "dashboard.noTransactions": "لا توجد معاملات بعد",
    "dashboard.allAccounts": "جميع الحسابات",
    "dashboard.filterByAccount": "تصفية حسب الحساب",
    "dashboard.account": "حساب",
    "verification.provisional": "مؤقتة",
    "verification.verified": "متحقق منها بكشف الحساب",
    "verification.disputed": "المبلغ محل اختلاف",
    "verification.disputedDescription": "يختلف المبلغ الملتقط عن التسوية النهائية في كشف الحساب. يُحتفظ بكلا المبلغين للمراجعة.",
    "verification.statementSettlement": "تسوية كشف الحساب: {amount}.",

    // Reconciliation
    "reconcile.title": "تسوية الرصيد",
    "reconcile.desc": "المعاملات المتتبعة لا تتطابق مع رصيدك الفعلي في البنك. أنشئ تعديلاً لسد الفجوة.",
    "reconcile.smsBalance": "رصيد البنك الفعلي (رسائل)",
    "reconcile.calculatedBalance": "الرصيد المحسوب",
    "reconcile.gap": "الفجوة",
    "reconcile.missingIncome": "دخل مفقود",
    "reconcile.missingExpense": "مصروف مفقود",
    "reconcile.balanced": "متوازن",
    "reconcile.balancedDesc": "المعاملات المتتبعة تتطابق مع رصيدك البنكي.",
    "reconcile.createAdjustment": "إنشاء معاملة تعديل",
    "reconcile.adjustmentCreated": "تم إنشاء تعديل التسوية بنجاح!",
    "reconcile.startingBalance": "الرصيد الابتدائي",
    "reconcile.totalCashIn": "إجمالي الدخل",
    "reconcile.totalCashOut": "إجمالي المصروفات",
    "reconcile.explanation": "سيتم إنشاء معاملة واحدة لتغطية المبلغ المفقود وموازنة رصيدك.",
    "reconcile.accountDesc": "تُقارن معاملات هذا الحساب فقط برصيده البنكي الخاص به.",
    "reconcile.allAccountsDesc": "تُسوّى كل الحسابات البنكية بشكل منفصل. «جميع الحسابات» ملخص وليست سلسلة رصيد مجمّعة.",
    "reconcile.allAccountsSummary": "اختر حساباً أعلاه للمراجعة أو لإنشاء تعديل. يُرحّل التعديل دائماً إلى حساب واحد محدد.",
    "reconcile.differenceFound": "تم العثور على فرق",
    "reconcile.notComparable": "غير قابل للمقارنة",
    "reconcile.notComparableDesc": "يحتاج هذا الحساب إلى رصيد بنكي افتتاحي وآخر رصيد قبل إمكان تسويته.",
    "reconcile.accountNeedsReview": "حساب واحد يحتاج إلى مراجعة",
    "reconcile.accountsNeedReview": "{{count}} حسابات تحتاج إلى مراجعة",
    "reconcile.reviewAccounts": "مراجعة الحسابات",
    "reconcile.reviewBalanceStatus": "مراجعة حالة الأرصدة",

    // Upload Transactions
    "sms.title": "رفع معلومات المعاملات",
    "sms.subtitle": "ارفع كشوف حسابات بنكية، الصق رسائل نصية، أو ارفع لقطات شاشة لاستخراج وتصنيف المعاملات",
    "sms.registeredBanks": "أرقام رسائل البنوك المسجلة",
    "sms.registeredBanksDesc": "أضف أرقام مرسلي رسائل البنك للكشف التلقائي عند لصق الرسائل",
    "sms.addBank": "إضافة بنك",
    "sms.editBank": "تعديل مرسل البنك",
    "sms.addBankTitle": "إضافة رقم رسائل البنك",
    "sms.senderNumber": "رقم / معرّف المرسل",
    "sms.senderNumberPlaceholder": "مثال: NBK، ‎+96590000000، KFH-SMS",
    "sms.senderNumberDesc": "اسم المرسل أو الرقم الذي يظهر في رسائل البنك النصية",
    "sms.bankName": "اسم البنك",
    "sms.bankNamePlaceholder": "مثال: بنك الكويت الوطني",
    "sms.bankCode": "رمز البنك (اختياري)",
    "sms.bankCodePlaceholder": "مثال: NBK، KFH، BOUBYAN",
    "sms.bankCodeDesc": "رمز مختصر يُستخدم لتصنيف المعاملات",
    "sms.save": "حفظ",
    "sms.cancel": "إلغاء",
    "sms.noBanks": "لم يتم تسجيل أرقام رسائل بنوك بعد",
    "sms.active": "نشط",
    "sms.inactive": "غير نشط",

    // SMS Input
    "sms.inputTitle": "رفع بيانات المعاملات",
    "sms.statementUploader": "كشف حساب بنكي",
    "sms.single": "رسالة واحدة",
    "sms.bulk": "رسائل متعددة",
    "sms.sms": "رسائل SMS",
    "sms.image": "صورة",
    "sms.pdf": "كشف حساب PDF",
    "sms.fileUpload": "رفع ملف",
    "sms.pasteHere": "الصق رسالة البنك النصية هنا...",
    "sms.pasteBulk": "الصق عدة رسائل نصية (مفصولة بأسطر فارغة)...",
    "sms.pasteSms": "الصق رسالة بنكية أو أكثر (افصل بين الرسائل بسطر فارغ)...",
    "sms.detectedBank": "البنك المكتشف:",
    "sms.parseAndSave": "تحليل وحفظ",
    "sms.parsing": "جاري التحليل...",
    "sms.reportIssue": "الإبلاغ عن مشكلة في التحليل",
    "sms.parseResult": "نتيجة التحليل",
    "sms.bulkResults": "نتائج التحليل المجمّع",
    "sms.parsed": "تم تحليلها",
    "sms.failed": "فشلت",
    "sms.duplicates": "مكررة",
    "sms.failedMessages": "الرسائل الفاشلة:",
    "sms.couldNotParse": "تعذر تحليل الرسالة",
    "sms.couldNotParseDesc": "لم يتم التعرف على صيغة الرسالة. جرّب صيغة مختلفة أو أبلغ عن المشكلة.",
    "sms.supportedFormats": "صيغ الرسائل المدعومة",

    // Image upload
    "sms.imageTitle": "رفع لقطة شاشة الرسائل",
    "sms.imageDesc": "ارفع لقطة شاشة لرسائل البنك النصية. سيقوم التطبيق بقراءة وتحليل كل رسالة تلقائياً.",
    "sms.dragDrop": "اسحب وأفلت صورة هنا، أو انقر للتصفح",
    "sms.supportedTypes": "يدعم PNG، JPG، JPEG، وكشوف حسابات PDF",
    "sms.maxSize": "الحد الأقصى لحجم الملف: 10 ميجابايت لكل ملف",
    "sms.uploadAndParse": "رفع وتحليل",
    "sms.uploading": "جاري الرفع...",
    "sms.processing": "جاري معالجة الصورة...",
    "sms.imageResults": "نتائج تحليل الصورة",
    "sms.pdfResults": "نتائج كشف الحساب",
    "sms.pdfProcessing": "جاري معالجة كشف الحساب...",
    "sms.pdfReconcileTitle": "جارٍ التحقق من استيراد كشف مكتمل",
    "sms.pdfReconcileDescription": "انتهى طلب المتصفح قبل أن يرد الخادم. قد يكون الاستيراد قد اكتمل بأمان.",
    "sms.pdfCompletedAfterReconnect": "اكتمل استيراد كشف الحساب: أضيفت {count} معاملة. تم تحديث لوحة التحكم.",
    "sms.pdfAlreadyImported": "تم استيراد هذا الكشف نفسه مسبقاً. لم يتم إنشاء معاملات مكررة.",
    "sms.pdfZeroRowsTitle": "لم يتم استخراج أي معاملات",
    "sms.pdfZeroRowsDescription": "وجدنا {expected} صفاً مشابهاً للمعاملات عبر {pages} صفحات، لكن لم نستخرج أي معاملة. لم يتم استيراد الكشف.",
    "sms.pdfTransportTitle": "انقطع طلب كشف الحساب",
    "sms.pdfTransportDescription": "قد يكمل الخادم العمل بأمان. نتحقق من الكشف قبل عرض إعادة المحاولة.",
    "sms.pdfRetry": "إعادة محاولة استيراد الكشف",
    "sms.pdfProtectedRecovered": "كان كشفك محمياً من النسخ. جهّز ميزانو الملف بأمان للقراءة ولم يحتفظ بنسخة مفكوكة الحماية.",
    "sms.pdfPasswordTitle": "يتطلب كشف الحساب كلمة مرور الفتح",
    "sms.pdfPasswordDescription": "حَمَى البنك ملف PDF هذا. أدخل كلمة المرور المستخدمة لفتح الكشف؛ تُستخدم لهذا الطلب فقط ولا يتم حفظها.",
    "sms.pdfPasswordLabel": "كلمة مرور كشف الحساب",
    "sms.pdfPasswordRequired": "أدخل كلمة مرور كشف الحساب للمتابعة",
    "sms.pdfRefreshImports": "التحقق من الاستيراد المكتمل",
    "sms.pdfControlPassed": "إجماليات التحقق: مطابقة",
    "sms.pdfControlUnavailable": "إجماليات التحقق: غير متاحة",
    "sms.pdfControlForcedMismatch": "إجماليات التحقق: تم تجاوز عدم المطابقة",
    "sms.pdfNotBankStatementTitle": "هذا الملف ليس كشف حساب بنكي",
    "sms.pdfNotBankStatementDescription": "لم يتم استيراده. لم يتم إنشاء سجل كشف أو مطالبة استيراد.",
    "sms.pdfClassificationEvidence": "دليل التصنيف:",
    "sms.pdfChooseAnother": "اختر مستنداً آخر",
    "sms.pdfPages": "صفحات تمت معالجتها",
    "sms.pdfTransactions": "معاملات مستخرجة",
    "sms.pdfBank": "البنك:",
    "sms.pdfCustomer": "العميل:",
    "sms.pdfPeriod": "الفترة:",
    "sms.deleteAll": "حذف جميع المعاملات",
    "sms.deleteAllConfirm": "هل أنت متأكد من حذف جميع معاملاتك؟ لا يمكن التراجع عن هذا الإجراء.",
    "sms.deleteAllSuccess": "تم حذف جميع المعاملات بنجاح",
    "sms.deleting": "جاري الحذف...",
    "sms.extractedMessages": "الرسائل المستخرجة:",
    "sms.bankDetected": "البنك المكتشف:",

    // Transaction fields
    "field.type": "النوع",
    "field.amount": "المبلغ",
    "field.merchant": "التاجر",
    "field.category": "الفئة",
    "field.date": "التاريخ",
    "field.balance": "الرصيد",
    "field.bankSource": "مصدر البنك",
    "field.notes": "ملاحظات",
    "field.cashIn": "وارد",
    "field.cashOut": "صادر",

    // Transactions page
    "transactions.title": "المعاملات",
    "transactions.subtitle": "عرض وإدارة جميع معاملاتك",
    "transactions.search": "البحث في المعاملات...",
    "transactions.allTypes": "جميع الأنواع",
    "transactions.allCategories": "جميع الفئات",
    "transactions.allBanks": "جميع البنوك",
    "transactions.noTransactions": "لم يتم العثور على معاملات",
    "transactions.noTransactionsDesc": "حلّل بعض الرسائل النصية لرؤية معاملاتك هنا.",
    "transactions.edit": "تعديل",
    "transactions.delete": "حذف",
    "transactions.editTitle": "تعديل المعاملة",
    "transactions.saveChanges": "حفظ التغييرات",

    // Income Statement
    "income.title": "كشف الدخل",
    "income.subtitle": "ملخص الدخل والمصروفات",
    "income.totalIncome": "إجمالي الداخل",
    "income.totalExpenses": "إجمالي الخارج",
    "income.netCashFlow": "صافي التدفق النقدي",
    "income.startingBalance": "الرصيد الافتتاحي",
    "income.currentBalance": "الرصيد الحالي",
    "income.noData": "لا توجد بيانات كشف الدخل بعد",
    "income.noDataDesc": "حلّل بعض الرسائل النصية لإنشاء كشف الدخل.",

    // Analytics
    "analytics.title": "التحليلات",
    "analytics.subtitle": "اتجاهات الإنفاق وتوزيع الفئات",
    "analytics.spendingTrend": "اتجاه الإنفاق",
    "analytics.categoryBreakdown": "توزيع الفئات",
    "analytics.noData": "لا توجد بيانات تحليلية بعد",
    "analytics.noDataDesc": "حلّل بعض الرسائل النصية لرؤية تحليلات الإنفاق.",

    // Budgets
    "budgets.title": "الميزانيات",
    "budgets.subtitle": "حدد حدود الإنفاق الشهرية لكل فئة",
    "budgets.addBudget": "إضافة ميزانية",
    "budgets.category": "الفئة",
    "budgets.limit": "الحد",
    "budgets.spent": "المنفق",
    "budgets.remaining": "المتبقي",
    "budgets.period": "الفترة",
    "budgets.monthly": "شهري",
    "budgets.weekly": "أسبوعي",
    "budgets.noBudgets": "لم يتم تحديد ميزانيات بعد",
    "budgets.noBudgetsDesc": "حدد حدود الميزانية لتتبع إنفاقك حسب الفئة.",
    "budgets.alerts": "تنبيهات الميزانية",
    "budgets.overBudget": "تجاوز الميزانية!",
    "budgets.createBudget": "إنشاء ميزانية",

    // Export
    "export.title": "البيانات المالية",
    "export.subtitle": "قوائم الدخل والتقارير المهنية",
    "export.csv": "تصدير CSV",
    "export.pdf": "تصدير PDF",
    "export.noData": "لا توجد بيانات للتصدير",
    "export.noDataDesc": "قم بتحميل بعض المعاملات أولاً لإنشاء البيانات المالية.",
    "export.monthly": "شهري",
    "export.quarterly": "ربع سنوي",
    "export.yearly": "سنوي",
    "export.statementOfIncome": "قائمة الدخل",
    "export.periodEnded": "الفترة المنتهية في",
    "export.revenue": "الداخل",
    "export.totalRevenue": "إجمالي الداخل",
    "export.expenses": "الخارج",
    "export.totalExpenses": "إجمالي الخارج",
    "export.netIncome": "صافي التدفق",
    "export.cashFlowSummary": "ملخص التدفق النقدي",
    "export.downloadPdf": "تحميل PDF",
    "export.downloadCsv": "تحميل CSV",
    "export.workspaceData": "تصدير مساحة العمل هذه",
    "export.workspaceExportReady": "تصدير بيانات مساحة العمل جاهز",
    "export.workspaceExportUnavailable": "تعذر إعداد بيانات مساحة العمل. يرجى محاولة التصدير مرة أخرى.",
    "export.workspaceScopeHint": "يحتوي هذا التنزيل على مساحة العمل النشطة فقط. استخدم إعدادات الحساب لتصدير جميع البيانات الشخصية والتجارية معاً.",
    "export.transactions": "معاملات",
    "export.q1": "ر1",
    "export.q2": "ر2",
    "export.q3": "ر3",
    "export.q4": "ر4",
    "export.currentPeriod": "الفترة الحالية",
    "export.kwdThousands": "د.ك",
    "export.unaudited": "غير مدقق",

    // Setup
    "setup.title": "الإعدادات",
    "setup.step1Title": "ملفك الشخصي",
    "setup.step1Desc": "أخبرنا قليلاً عن نفسك",
    "setup.step2Title": "أرقام رسائل البنك",
    "setup.step2Desc": "قم بتكوين أرقام مرسلي رسائل البنك",
    "setup.name": "الاسم الكامل",
    "setup.phone": "رقم الهاتف",
    "setup.next": "التالي",
    "setup.back": "رجوع",
    "setup.finish": "إنهاء الإعداد",
    "setup.skip": "تخطي الآن",

    // Common
    "common.loading": "جارٍ التحميل...",
    "common.retry": "إعادة المحاولة",
    "common.error": "خطأ",
    "common.success": "نجاح",
    "common.confirm": "تأكيد",
    "common.cancel": "إلغاء",
    "common.save": "حفظ",
    "common.delete": "حذف",
    "common.edit": "تعديل",
    "common.close": "إغلاق",
    "common.search": "بحث",
    "common.filter": "تصفية",
    "common.noData": "لا توجد بيانات",
    "common.yes": "نعم",
    "common.no": "لا",
    "invoice.needsAmending": "بحاجة إلى تعديل",
    "invoice.matching": "المطابقة",
    "common.viewAll": "عرض الكل",

    // Success popup
    "success.uploadComplete": "اكتمل الرفع!",
    "success.singleParsed": "تم تحليل المعاملة وحفظها بنجاح.",
    "success.bulkParsed": "تم تحليل {parsed} معاملة. {failed} فشلت. {duplicates} مكررة.",
    "success.imageParsed": "تم استخراج {parsed} معاملة من الصورة. {failed} فشلت. {duplicates} مكررة.",
    "success.pdfParsed": "تم استخراج {parsed} معاملة من {pages} صفحة. {duplicates} مكررة.",
    "success.pdfAllDuplicates": "تم استخراج {total} معاملة من {pages} صفحة. جميعها مكررة (تم استيرادها مسبقاً).",
    "success.viewTransactions": "عرض المعاملات",
    "success.uploadMore": "رفع المزيد",
    "success.done": "تم",
    "success.deleteComplete": "تم حذف جميع المعاملات",
    "success.deleteDesc": "تم حذف جميع بيانات المعاملات نهائياً.",
  },
};

// Arabic category labels
export const CATEGORY_LABELS_AR: Record<string, string> = {
  salary: "راتب",
  freelance: "عمل حر",
  investment: "استثمار",
  refund: "استرداد",
  transfer_in: "تحويل وارد",
  food: "طعام ومطاعم",
  groceries: "بقالة",
  bills: "فواتير وخدمات",
  telecom: "اتصالات",
  transport: "مواصلات",
  entertainment: "ترفيه",
  shopping: "تسوق",
  healthcare: "رعاية صحية",
  education: "تعليم",
  rent: "إيجار",
  insurance: "تأمين",
  transfer_out: "تحويل صادر",
  withdrawal: "سحب نقدي",
  credit_card_payment: "دفع بطاقة ائتمان",
  loan_payment: "دفعة قرض",
  atm_withdrawal: "سحب صراف آلي",
  fees_charges: "رسوم ومصاريف",
  other: "أخرى",
  uncategorized: "غير مصنف",
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem(LANGUAGE_KEY);
    return (saved === "ar" || saved === "en") ? saved : "en";
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_KEY, lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === "en" ? "ar" : "en");
  }, [language, setLanguage]);

  const t = useCallback(
    (key: string): string => {
      return translations[language][key] ?? translations.en[key] ?? key;
    },
    [language]
  );

  const dir = language === "ar" ? "rtl" : "ltr";
  const isRTL = language === "ar";

  // Apply dir attribute to html element
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [dir, language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, dir, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
